import React, { useState, useEffect, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import { useLocation } from "react-router-dom";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Photo as PhotoIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon,
  Download as DownloadIcon,
  Build as BuildIcon,
  CleaningServices as CleaningIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  Event as EventIcon,
  Engineering as EngineeringIcon,
  Notes as NotesIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  HourglassEmpty as PendingIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { getAuthHeader } from "../../helpers/auth";
import axios from "axios";
import { X, ChevronRight, Activity, CheckCircle, Clock, Calendar } from "lucide-react";

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:       "var(--nd-bg)",
  surface:  "var(--nd-surface)",
  surf2:    "var(--nd-surface-2)",
  surf3:    "var(--nd-surface-2)",
  border:   "var(--nd-w08)",
  bord2:    "var(--nd-w10)",
  text:     "var(--nd-text)",
  muted:    "var(--nd-tx35)",
  faint:    "var(--nd-w10)",
  blue:     "#3B82F6",
  teal:     "#22D3A5",
  amber:    "#FBBF24",
  red:      "#F87171",
  purple:   "#A78BFA",
  pink:     "#F472B6",
  green:    "#34D399",
  font:     "'DM Sans', sans-serif",
  mono:     "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
  @keyframes toastIn { from{opacity:0;transform:translate(-50%,20px)} to{opacity:1;transform:translate(-50%,0)} }

  .jh-job-card {
    animation: fadeUp 0.32s ease both;
    transition: transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s;
  }
  .jh-job-card:hover { transform: translateY(-5px); box-shadow: 0 20px 44px rgba(0,0,0,0.5) !important; }

  .jh-nav-card {
    background: var(--nd-surface); border: 1px solid var(--nd-w08);
    border-radius: 14px; padding: 18px 20px; cursor: pointer;
    display: flex; align-items: center; gap: 14px;
    transition: all 0.18s; animation: fadeUp 0.3s ease both;
  }
  .jh-nav-card:hover {
    border-color: rgba(59,130,246,0.4); background: rgba(59,130,246,0.06);
    transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  }

  .jh-stat-card { transition: transform 0.2s, box-shadow 0.2s; }
  .jh-stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.4) !important; }

  .jh-filter-btn {
    padding: 7px 15px; border-radius: 8px; font-size: 12px; font-weight: 700;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
    border: 1px solid var(--nd-w08); transition: all 0.15s;
    background: var(--nd-w04); color: var(--nd-tx40);
    display: flex; align-items: center; gap: 5px;
  }
  .jh-filter-btn:hover { background: var(--nd-w08); color: var(--nd-tx70); }
  .jh-filter-btn.jh-active-all    { background: var(--nd-w08); color: var(--nd-text); border-color: var(--nd-w20); }
  .jh-filter-btn.jh-active-comm   { background: rgba(59,130,246,0.14); color: #60A5FA; border-color: rgba(59,130,246,0.32); }
  .jh-filter-btn.jh-active-maint  { background: rgba(167,139,250,0.14); color: #A78BFA; border-color: rgba(167,139,250,0.32); }
  .jh-filter-btn.jh-active-done   { background: rgba(52,211,153,0.12); color: #34D399; border-color: rgba(52,211,153,0.28); }
  .jh-filter-btn.jh-active-prog   { background: rgba(251,191,36,0.12); color: #FBBF24; border-color: rgba(251,191,36,0.28); }
  .jh-filter-btn.jh-active-sched  { background: rgba(59,130,246,0.12); color: #60A5FA; border-color: rgba(59,130,246,0.28); }
  .jh-filter-btn.jh-active-rej    { background: rgba(248,113,113,0.12); color: #F87171; border-color: rgba(248,113,113,0.28); }

  .jh-input {
    width: 100%; padding: 9px 11px;
    background: var(--nd-w05); border: 1px solid var(--nd-w10);
    border-radius: 9px; color: var(--nd-text); font-size: 13px;
    font-family: 'DM Sans', sans-serif; outline: none; box-sizing: border-box;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .jh-input:focus { border-color: rgba(96,165,250,0.5); box-shadow: 0 0 0 3px rgba(96,165,250,0.10); background: var(--nd-w08); }
  .jh-input::placeholder { color: var(--nd-w20); }
  .jh-input option { background: #14161c; color: var(--nd-text); }

  .jh-label {
    display: block; font-size: 9px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); margin-bottom: 5px;
  }

  .jh-btn-primary {
    padding: 10px 20px; border: none; border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong);
    display: flex; align-items: center; justify-content: center; gap: 6px;
    box-shadow: 0 4px 14px rgba(59,130,246,0.28); transition: opacity 0.15s, transform 0.15s;
  }
  .jh-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
  .jh-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .jh-btn-ghost {
    padding: 10px 20px; border: 1px solid var(--nd-w12); border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
    background: var(--nd-w04); color: var(--nd-tx60);
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: background 0.15s;
  }
  .jh-btn-ghost:hover { background: var(--nd-w08); color: var(--nd-text); }
  .jh-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

  /* CI-only: approve / reject / undo actions */
  .jh-btn-approve, .jh-btn-reject, .jh-btn-undo {
    padding: 9px 16px; border-radius: 9px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: all 0.15s;
  }
  .jh-btn-approve { background: rgba(52,211,153,0.12);  color: #34D399; border: 1px solid rgba(52,211,153,0.30); }
  .jh-btn-approve:hover { background: rgba(52,211,153,0.20); }
  .jh-btn-reject  { background: rgba(248,113,113,0.12); color: #F87171; border: 1px solid rgba(248,113,113,0.30); }
  .jh-btn-reject:hover  { background: rgba(248,113,113,0.20); }
  .jh-btn-undo    { background: var(--nd-w04); color: var(--nd-tx60); border: 1px solid var(--nd-w12); }
  .jh-btn-undo:hover    { background: var(--nd-w08); color: var(--nd-text); }
  .jh-btn-approve:disabled, .jh-btn-reject:disabled, .jh-btn-undo:disabled { opacity: 0.5; cursor: not-allowed; }

  /* CI-only: browse-mode segmented toggle */
  .jh-seg {
    display: inline-flex; gap: 4px; padding: 4px;
    background: var(--nd-w04); border: 1px solid var(--nd-w08); border-radius: 11px;
  }
  .jh-seg-btn {
    padding: 7px 18px; border-radius: 8px; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    background: transparent; color: var(--nd-tx40); transition: all 0.15s;
    display: flex; align-items: center; gap: 6px;
  }
  .jh-seg-btn:hover { color: var(--nd-tx70); }
  .jh-seg-btn.jh-seg-on {
    background: var(--nd-w10); color: var(--nd-text);
    box-shadow: 0 2px 8px rgba(0,0,0,0.30);
  }

  .jh-field-cell {
    background: var(--nd-w03); border: 1px solid var(--nd-w08);
    border-radius: 10px; padding: 12px 14px;
  }
  .jh-check-cell {
    display: flex; align-items: center; justify-content: space-between;
    background: var(--nd-w03); border: 1px solid var(--nd-w08);
    border-radius: 10px; padding: 10px 14px; transition: all 0.15s;
  }
  .jh-check-cell.active-check {
    background: rgba(52,211,153,0.08); border-color: rgba(52,211,153,0.25);
  }
  .jh-timeline-dot {
    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ── Helpers ───────────────────────────────────────────────────────
const getStatusCfg = (status) => {
  switch (status) {
    case "completed":
      return { color: "#34D399", bg: "rgba(52,211,153,0.10)",  border: "rgba(52,211,153,0.26)",  label: "Completed"   };
    case "started": case "in_progress": case "before_photos": case "after_photos":
      return { color: "#FBBF24", bg: "rgba(251,191,36,0.10)",  border: "rgba(251,191,36,0.26)",  label: "In Progress" };
    case "scheduled":
      return { color: "#60A5FA", bg: "rgba(59,130,246,0.10)",  border: "rgba(59,130,246,0.26)",  label: "Scheduled"   };
    default:
      return { color: "#94A3B8", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.20)", label: status        };
  }
};

const getTypeCfg = (type) =>
  type === "maintenance"
    ? { color: "#A78BFA", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.28)", label: "Maintenance"  }
    : { color: "#60A5FA", bg: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.28)",  label: "Commercial"   };

const getStatusIcon = (status) =>
  status === "completed"
    ? "✓"
    : ["started", "in_progress", "before_photos", "after_photos"].includes(status)
      ? "⏳"
      : "📅";

// Approval badge — dark-theme palette
const getApprovalBadge = (approval_status) => {
  switch (approval_status) {
    case "approved": return { label: "Approved", color: "#34D399", bg: "rgba(52,211,153,0.10)",  border: "rgba(52,211,153,0.26)",  icon: "✓" };
    case "rejected": return { label: "Rejected", color: "#F87171", bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.26)", icon: "✕" };
    default:         return { label: "Pending",  color: "#FBBF24", bg: "rgba(251,191,36,0.10)",  border: "rgba(251,191,36,0.26)",  icon: "⏳" };
  }
};

const Overlay = ({ onClick, children, zIndex = 9999 }) => (
  <div onClick={onClick} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)",
    backdropFilter: "blur(5px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex, padding: 20,
  }}>{children}</div>
);

const CloseBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    background: "var(--nd-w06)", border: `1px solid ${T.border}`,
    color: T.muted, width: 32, height: 32, borderRadius: 8, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    transition: "background 0.15s",
  }}
    onMouseEnter={e => e.currentTarget.style.background = "var(--nd-w12)"}
    onMouseLeave={e => e.currentTarget.style.background = "var(--nd-w06)"}
  ><X size={14} /></button>
);

const SecHead = ({ icon: Icon, label, color, right }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, border: `1px solid ${color}35`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon style={{ fontSize: 15, color }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{label}</span>
    {right && <div style={{ marginLeft: "auto" }}>{right}</div>}
  </div>
);

const FieldCell = ({ label, value, span }) => (
  <div className="jh-field-cell" style={span ? { gridColumn: "1 / -1" } : {}}>
    <div className="jh-label">{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{value || "N/A"}</div>
  </div>
);

// 🔥 AUTO-FALLBACK IMAGE COMPONENT (Folder finding logic)
const AutoFallbackImage = ({ photoRaw, alt, isEditing, onDelete, onClick, customStyle }) => {
  const [errorCount, setErrorCount] = React.useState(0);
  
  // Har kism ke format (JSON, Object, Array, String) ko clean karna
  let cleanStr = "";
  if (photoRaw && typeof photoRaw === 'object') {
    cleanStr = photoRaw.url || photoRaw.path || photoRaw.name || String(photoRaw);
  } else {
    cleanStr = String(photoRaw);
  }
  
  // Extra quotes ya brackets clean karna
  cleanStr = cleanStr.replace(/["\[\]\\]/g, "").replace(/%22/g, "").replace(/%5D/g, "").replace(/%5B/g, "").trim();

  // 96% of job photos are stored as an absolute FILESYSTEM path
  // (/var/www/sowashapp/backend/sowash-backend/uploads/job-photos/x.jpg) rather
  // than a URL. Served as-is that hits the SPA catch-all and returns index.html
  // with a 200, so the <img> only recovers on the next candidate — one wasted
  // round-trip per photo. Trim anything before /uploads/ so the first candidate
  // is already correct.
  const uploadsAt = cleanStr.indexOf("/uploads/");
  if (uploadsAt > 0) cleanStr = cleanStr.slice(uploadsAt);

  let filename = cleanStr.split("/").pop();

  // Sab possible paths ki list
  const candidates = [];
  if (cleanStr.startsWith("http")) candidates.push(cleanStr);
  if (cleanStr.startsWith("/")) candidates.push(`https://app.sowashusa.com${cleanStr}`);
  
  candidates.push(`https://app.sowashusa.com/uploads/job-photos/${filename}`);
  candidates.push(`https://app.sowashusa.com/uploads/maintenance/${filename}`);
  candidates.push(`https://app.sowashusa.com/uploads/sld-reports/${filename}`); // Agar naya folder ho
  candidates.push(`https://app.sowashusa.com/uploads/signatures/${filename}`);
  
  // Duplicate paths hatana
  const uniqueCandidates = [...new Set(candidates)];
  const currentSrc = uniqueCandidates[errorCount] || uniqueCandidates[uniqueCandidates.length - 1];

  const defaultStyle = { width: "100%", height: "150px", objectFit: "cover", borderRadius: "12px", cursor: isEditing ? "default" : "pointer", border: `1px solid ${T.border}`, background: "var(--nd-w03)" };

  return (
    <div style={{ position: 'relative' }}>
      <img
        src={currentSrc}
        alt={alt}
        style={customStyle || defaultStyle}
        onClick={() => {
          if (onClick) onClick(currentSrc);
          else if (!isEditing) window.open(currentSrc, "_blank");
        }}
        onError={() => {
          // Agar image na milay tou aglay folder mein khud check karega
          if (errorCount < uniqueCandidates.length - 1) {
            setErrorCount(prev => prev + 1);
          }
        }}
      />
      {isEditing && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{ position: 'absolute', top: '5px', right: '5px', background: 'rgba(248,113,113,0.92)', color: '#0B0D11', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
        >✕</button>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
//   JobEfficiencyTimer
//   Module scope on purpose: defining this inside JobHistory made React
//   treat it as a new component type on every parent render, remounting
//   the subtree and killing the running setInterval.
// ══════════════════════════════════════════════════════════════════
const SHIFT_CONFIG = {
  morning:   { label: 'Shift: 7 AM',  startHour: 7  },
  afternoon: { label: 'Shift: 11 PM', startHour: 11 },
  evening:   { label: 'Shift: 3 PM',  startHour: 15 },
  night:     { label: 'Shift: 7 PM',  startHour: 19 },
  overnight: { label: 'Shift: 10 PM', startHour: 22 },
};

const getShiftConfig = (shift) => SHIFT_CONFIG[shift] || { label: 'Shift: N/A', startHour: null };

const JobEfficiencyTimer = ({ job }) => {
  const [timerData, setTimerData] = useState({
    remainingTime: 0,
    isExceeded: false,
    arrivalDiffMinutes: 0,
    totalTimeTaken: 0,
    efficiencyScore: 0
  });

  useEffect(() => {
    const calculate = () => {
      if (!job.started_at) return;

      // 1. Shift Arrival Logic
      const actualReached = new Date(job.reached_location_at);
      const shiftStartTime = new Date(actualReached);

      const shiftConfig = getShiftConfig(job.shift);
      if (shiftConfig.startHour !== null) {
        shiftStartTime.setHours(shiftConfig.startHour, 0, 0, 0);
      }

      // positive = late, negative = early, 0 = on time
      const diffMs = actualReached - shiftStartTime;
      const arrivalDiffMinutes = Math.floor(diffMs / 60000);

      // 2. Efficiency Logic
      const teamSize = 4;
      const totalManHours = parseFloat(job.total_man_hours) || 32;
      const targetSec = (totalManHours / teamSize) * 3600;

      const endTime = job.completed_at ? new Date(job.completed_at) : new Date();
      const elapsedSec = Math.floor((endTime - new Date(job.reached_location_at)) / 1000);
      const remaining = targetSec - elapsedSec;
      const efficiency = elapsedSec > 0 ? Math.round((targetSec / elapsedSec) * 100) : 100;

      setTimerData({
        remainingTime: Math.abs(remaining),
        isExceeded: remaining < 0,
        arrivalDiffMinutes,
        totalTimeTaken: elapsedSec,
        efficiencyScore: efficiency
      });
    };

    if (job.completed_at) {
      calculate();
    } else {
      const itv = setInterval(calculate, 1000);
      calculate();
      return () => clearInterval(itv);
    }
  }, [job]);

  const formatDisplayTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDelay = (totalMins) => {
    if (totalMins < 60) return `${totalMins}m`;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const getArrivalStatus = () => {
    const diff = timerData.arrivalDiffMinutes;
    if (diff > 0)  return { label: `${formatDelay(diff)} Late`,            color: T.amber, bg: "rgba(251,191,36,0.08)",  border: "rgba(251,191,36,0.24)" };
    if (diff < 0)  return { label: `${formatDelay(Math.abs(diff))} Early`, color: T.blue,  bg: "rgba(59,130,246,0.08)",  border: "rgba(59,130,246,0.24)" };
                   return { label: 'On Time',                              color: T.green, bg: "rgba(52,211,153,0.08)",  border: "rgba(52,211,153,0.24)" };
  };

  if (!job.started_at) return (
    <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "var(--nd-w03)", border: "1px dashed var(--nd-w10)", textAlign: "center" }}>
      <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>🕒 Waiting to start…</span>
    </div>
  );

  const isDone  = !!job.completed_at;
  const exceed  = timerData.isExceeded;
  const arrival = getArrivalStatus();

  const timerColor = isDone ? T.text : (exceed ? T.red : T.green);
  const timerBg    = isDone ? "var(--nd-w03)" : (exceed ? "rgba(248,113,113,0.07)" : "rgba(52,211,153,0.07)");
  const timerBrd   = isDone ? T.border : (exceed ? "rgba(248,113,113,0.22)" : "rgba(52,211,153,0.22)");
  const effOk      = timerData.efficiencyScore >= 100;

  return (
    <div style={{ marginTop: 10, padding: 14, borderRadius: 12, background: timerBg, border: `1px solid ${timerBrd}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 800, color: timerColor, textTransform: "uppercase", letterSpacing: "0.10em", marginBottom: 3 }}>
            {isDone ? "🏁 Total Time Taken" : (exceed ? "⚠️ Overtime" : "⏱️ Time Remaining")}
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: timerColor, fontFamily: T.mono, lineHeight: 1 }}>
            {isDone
              ? formatDisplayTime(timerData.totalTimeTaken)
              : (exceed
                  ? `-${formatDisplayTime(timerData.remainingTime)}`
                  : formatDisplayTime(timerData.remainingTime))}
          </div>
        </div>

        {isDone && (
          <div style={{
            textAlign: "right", padding: "5px 11px", borderRadius: 9,
            background: effOk ? "rgba(52,211,153,0.10)" : "rgba(248,113,113,0.10)",
            border: `1px solid ${effOk ? "rgba(52,211,153,0.26)" : "rgba(248,113,113,0.26)"}`,
          }}>
            <div style={{ fontSize: 8, fontWeight: 800, color: T.muted, letterSpacing: "0.10em" }}>EFFICIENCY</div>
            <div style={{ fontSize: 15, fontWeight: 900, color: effOk ? T.green : T.red, fontFamily: T.mono }}>{timerData.efficiencyScore}%</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 9 }}>
        {/* Arrival Status Card */}
        <div style={{ flex: 1, padding: "5px 9px", borderRadius: 7, background: arrival.bg, border: `1px solid ${arrival.border}` }}>
          <div style={{ fontSize: 8, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {getShiftConfig(job.shift).label}
          </div>
          <div style={{ color: arrival.color, fontSize: 12, fontWeight: 800, fontFamily: T.mono }}>
            {arrival.label}
          </div>
        </div>

        {/* Time Limit Card */}
        <div style={{ flex: 1, padding: "5px 9px", borderRadius: 7, background: "var(--nd-w03)", border: `1px solid ${T.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 8, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Limit (4 pax)</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, fontFamily: T.mono }}>
            {(parseFloat(job.total_man_hours || 32) / 4)}h
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
//   MAIN CI COMPONENT
// ══════════════════════════════════════════════════════════════════
const JobHistory = () => {
  const config = getAuthHeader();
  const location = useLocation();

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    scheduled: 0,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Approval state ──────────────────────────────────────────
  const [filterApproval, setFilterApproval]     = useState("all");
  const [approving, setApproving]               = useState(false);
  const [rejecting, setRejecting]               = useState(false);
  const [showRejectInput, setShowRejectInput]   = useState(false);
  const [rejectReason, setRejectReason]         = useState("");

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Approval handlers ───────────────────────────────────────
  const handleApprove = async () => {
    if (!selectedJob) return;
    setApproving(true);
    try {
      await axios.put(
        `/api/ci-admin/job-approvals/${selectedJob.schedule_id}/approve`,
        {},
        config
      );
      showToast("success", "✅ Job approved — now visible to client");
      setSelectedJob(prev => ({ ...prev, approval_status: "approved" }));
      fetchJobs();
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to approve job");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedJob) return;
    setRejecting(true);
    try {
      await axios.put(
        `/api/ci-admin/job-approvals/${selectedJob.schedule_id}/reject`,
        { reason: rejectReason.trim() || null },
        config
      );
      showToast("success", "🚫 Job rejected — hidden from client");
      setSelectedJob(prev => ({ ...prev, approval_status: "rejected", rejection_reason: rejectReason.trim() || null }));
      setShowRejectInput(false);
      setRejectReason("");
      fetchJobs();
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to reject job");
    } finally {
      setRejecting(false);
    }
  };

  const handleUndoApproval = async () => {
    if (!selectedJob) return;
    try {
      await axios.put(
        `/api/ci-admin/job-approvals/${selectedJob.schedule_id}/undo`,
        {},
        config
      );
      showToast("success", "↩️ Approval reset to pending");
      setSelectedJob(prev => ({ ...prev, approval_status: "pending", rejection_reason: null }));
      fetchJobs();
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to undo approval");
    }
  };

  // Pakistan Standard Time is UTC+5 with no DST.
  // All date display and save operations are pinned to PKT so that neither
  // the browser's OS timezone nor the VPS server timezone affects the result.
  const PKT_OFFSET_MS = 5 * 60 * 60 * 1000;

  // Return an unambiguous UTC millisecond value from any DB timestamp string.
  // Handles: "2026-04-21T06:19:00.000Z", "+05:00", no-tz (treated as UTC).
  const toUtcMs = (ds) => {
    if (!ds) return NaN;
    let s = String(ds).trim().replace(' ', 'T').split('.')[0];
    if (!s.includes('Z') && !s.includes('+') && !/\d{2}:\d{2}$/.test(s.slice(-6))) {
      s += 'Z'; // no tz info at all -> assume UTC
    }
    return new Date(s).getTime();
  };

  // DB timestamp -> datetime-local string in PKT
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    const pktMs = toUtcMs(dateString) + PKT_OFFSET_MS;
    if (isNaN(pktMs)) return '';
    const d = new Date(pktMs);
    return [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, '0'),
      String(d.getUTCDate()).padStart(2, '0')
    ].join('-') + 'T' + [
      String(d.getUTCHours()).padStart(2, '0'),
      String(d.getUTCMinutes()).padStart(2, '0')
    ].join(':');
  };

  // DB timestamp -> date-only string in PKT
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const pktMs = toUtcMs(dateString) + PKT_OFFSET_MS;
    if (isNaN(pktMs)) return '';
    const d = new Date(pktMs);
    return [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, '0'),
      String(d.getUTCDate()).padStart(2, '0')
    ].join('-');
  };

  // datetime-local string (user entered in PKT) -> UTC ISO string for DB
  const formatDateTimeForDB = (dateString) => {
    if (!dateString) return null;
    if (dateString.includes('Z') || dateString.includes('+')) return dateString;
    const [datePart, timePart] = dateString.split('T');
    if (!datePart || !timePart) return dateString;
    const [Y, M, D] = datePart.split('-').map(Number);
    const [h, mi] = timePart.split(':').map(Number);
    return new Date(Date.UTC(Y, M - 1, D, h, mi) - PKT_OFFSET_MS).toISOString();
  };

  const handleUpdate = async () => {
  setSaving(true);
  try {
    const payload = { ...editData };

    // Strip large photo arrays (managed via dedicated photo routes)
    ['before_photos','after_photos','temp_voltage_photos',
     'before_photo_url','after_photo_url','annotated_photo','annotated_photo_uri']
      .forEach(f => delete payload[f]);

    // Convert user-edited datetime-local fields (no tz suffix) from PKT -> UTC.
    // Fields that already have Z or + are left as-is.
    ['reached_location_at','started_at','before_photos_at','before_photo_at',
     'after_photos_at','after_photo_at','completed_at'].forEach(field => {
      const val = payload[field];
      if (val && typeof val === 'string' && val.includes('T') && !val.includes('Z') && !val.includes('+')) {
        payload[field] = formatDateTimeForDB(val);
      }
    });

    const response = await axios.put(
      `/api/schedule/edit/${editData.schedule_id}`,
      payload,
      config
    );

    if (response.data.success) {
      showToast('success', 'Job updated successfully');
      setIsEditing(false);
      setDetailsOpen(false);
      fetchJobs();
    }
  } catch (err) {
    console.error(err);
    showToast('error', 'Error updating job: ' + (err.response?.data?.message || err.message));
  } finally {
    setSaving(false);
  }
};

  // Upload image function
  const handleImageUpload = async (file, photoType) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('schedule_id', editData.schedule_id);
    formData.append('photo_type', photoType);
    try {
      const response = await axios.post('/api/schedule/edit/upload-photo', formData, {
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        let existing = [];
        try {
          existing = typeof editData[photoType] === 'string'
            ? JSON.parse(editData[photoType]) : (editData[photoType] || []);
        } catch (e) { existing = []; }
        existing.push(response.data.photoPath);
        setEditData({ ...editData, [photoType]: JSON.stringify(existing) });
        showToast('success', 'Photo uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('error', 'Error uploading photo: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Persist photo deletion immediately so reloading does not restore deleted photos
  const handlePhotoDelete = async (key, updatedPhotos) => {
    setEditData(prev => ({ ...prev, [key]: JSON.stringify(updatedPhotos) }));
    try {
      await axios.put(
        `/api/schedule/edit/${editData.schedule_id}/photos`,
        { photo_type: key, photos: updatedPhotos },
        config
      );
      showToast('success', 'Photo removed');
    } catch (err) {
      console.error('Photo delete error:', err);
      showToast('error', 'Failed to remove photo');
    }
  };

  useEffect(() => {
    if (selectedJob) {
      setEditData({ ...selectedJob });
    }
  }, [selectedJob]);

  const [viewMode, setViewMode] = useState("clients");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRange, setDateRange] = useState("all");
  const [browseMode, setBrowseMode] = useState("byClient");
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState({});
  const [bulkDone, setBulkDone] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramClient = searchParams.get("client");
    const paramDate = searchParams.get("date");
    const paramSite = searchParams.get("site");

    if (paramClient && paramDate) {
      const dateObj = new Date(paramDate);
      if (!isNaN(dateObj)) {
        const monthStr = format(dateObj, "yyyy-MM");
        const dateStr = format(dateObj, "yyyy-MM-dd");

        setSelectedClient(paramClient);
        setSelectedMonth(monthStr);
        setSelectedDate(dateStr);
        setViewMode("jobs");

        if (paramSite) {
          setSearchQuery(paramSite);
        }
      }
    } else if (paramClient) {
      setSelectedClient(paramClient);
      setViewMode("months");
    }
  }, [location.search]);

  const cleanPhotoUrl = (url) => {
    if (!url) return "";
    let cleanUrl = url
      .toString()
      .trim()
      .replace(/["\[\]]/g, "")
      .replace(/%22/g, "")
      .replace(/%5D/g, "");
    if (cleanUrl.startsWith("http")) return cleanUrl;
    if (cleanUrl.startsWith("/")) return `https://app.sowashusa.com${cleanUrl}`;
    return `https://app.sowashusa.com/uploads/maintenance/${cleanUrl}`;
  };

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchJobs();
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [filterStatus, dateRange, searchQuery, filterType, filterApproval]);

  const fetchJobs = async () => {
    try {
      setLoading(true);

      const storedUser = localStorage.getItem("user");
      let clientId = null;

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        clientId = parsedUser.client_id || parsedUser.id;
      }

      const params = {};

      if (clientId) {
        params.client_id = clientId;
      }

      if (filterStatus && filterStatus !== "all") {
        params.status = filterStatus;
      }

      if (filterType && filterType !== "all") {
        params.job_type = filterType;
      }

      if (filterApproval && filterApproval !== "all") {
        params.approval_status = filterApproval;
      }

      if (dateRange && dateRange !== "all") {
        params.dateRange = dateRange;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.search = searchQuery.trim();
      }

      const response = await axios.get("/api/schedule/history", {
        params,
        ...config,
      });
      setJobs(response.data.jobs);

      try {
        const API = process.env.REACT_APP_API_BASE_URL || '/api';
        const attRes = await axios.get(`${API}/attendance/records`);
        setAllAttendance(attRes.data?.records || []);
      } catch (e) {
        console.error("Error fetching attendance:", e);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/schedule/stats", config);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const clientGroups = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      const name = job.client_name || "Unknown Client";
      if (!groups[name]) groups[name] = [];
      groups[name].push(job);
    });
    return groups;
  }, [jobs]);

  const monthGroups = useMemo(() => {
    if (!selectedClient || !clientGroups[selectedClient]) return {};
    const groups = {};
    clientGroups[selectedClient].forEach((job) => {
      const monthKey = format(new Date(job.scheduled_date), "yyyy-MM");
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(job);
    });
    return groups;
  }, [selectedClient, clientGroups]);

  const dateGroups = useMemo(() => {
    if (!selectedMonth || !monthGroups[selectedMonth]) return {};
    const groups = {};
    monthGroups[selectedMonth].forEach((job) => {
      const dateKey = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(job);
    });
    return groups;
  }, [selectedMonth, monthGroups]);

  const currentJobsList = useMemo(() => {
    if (!selectedDate || !dateGroups[selectedDate]) return [];
    return dateGroups[selectedDate];
  }, [selectedDate, dateGroups]);

  // Groups ALL jobs across all clients by date — used by the "By Date" browse mode
  const allDateGroups = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      const dateKey = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(job);
    });
    return groups;
  }, [jobs]);

  const handleClientClick = (clientName) => {
    setSelectedClient(clientName);
    setViewMode("months");
  };

  const handleMonthClick = (monthKey) => {
    setSelectedMonth(monthKey);
    setViewMode("dates");
  };

  const handleDateClick = (dateKey) => {
    setSelectedDate(dateKey);
    setViewMode("jobs");
  };

  const handleBack = () => {
    if (browseMode === "byDate") {
      setSelectedDate(null);
      setBulkDownloadProgress({});
      setBulkDone(false);
    } else {
      if (viewMode === "jobs") {
        setViewMode("dates");
        setSelectedDate(null);
      } else if (viewMode === "dates") {
        setViewMode("months");
        setSelectedMonth(null);
      } else if (viewMode === "months") {
        setViewMode("clients");
        setSelectedClient(null);
        setSearchQuery("");
      }
    }
  };

  const handleDownloadAllReports = async () => {
    const jobsList = allDateGroups[selectedDate] || [];
    if (!jobsList.length) return;
    setBulkDownloading(true);
    setBulkDone(false);
    const init = {};
    jobsList.forEach((j) => { init[j.schedule_id] = "pending"; });
    setBulkDownloadProgress(init);

    for (const job of jobsList) {
      setBulkDownloadProgress((prev) => ({ ...prev, [job.schedule_id]: "downloading" }));
      try {
        const response = await axios.get(`/api/fo-schedules/${job.schedule_id}/detail`, config);
        const fullJob = response.data.job;
        await generateAndDownloadPDF(fullJob);
        setBulkDownloadProgress((prev) => ({ ...prev, [job.schedule_id]: "done" }));
      } catch (err) {
        console.error("Error downloading report for job", job.schedule_id, err);
        setBulkDownloadProgress((prev) => ({ ...prev, [job.schedule_id]: "error" }));
      }
      await new Promise((r) => setTimeout(r, 400));
    }
    setBulkDone(true);
    setBulkDownloading(false);
    setTimeout(() => { setBulkDownloadProgress({}); setBulkDone(false); }, 4000);
  };

  const getJobAttendance = (job) => {
    if (!job) return [];
    return allAttendance.filter(r => {
      if (r.schedule_id && r.schedule_id === job.schedule_id) return true;
      if (r.job_id && r.job_id === job.schedule_id) return true;
      const jobDate = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      return r.client_name === job.client_name && r.site_name === job.site_name && (r.attendance_date === jobDate || (r.attendance_date && r.attendance_date.startsWith(jobDate)));
    });
  };

  const handleViewDetails = async (job) => {
    try {
      setDetailsLoading(true);
      setDetailsOpen(true);
      const response = await axios.get(
        `/api/fo-schedules/${job.schedule_id}/detail`,
        config,
      );
      setSelectedJob(response.data.job);
    } catch (error) {
      console.error("Error fetching job details:", error);
      showToast('error', error.response?.data?.message || error.message);
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // PDF Logic — shared between single download and bulk download
// ─────────────────────────────────────────────────────────────────
// DROP-IN REPLACEMENT — paste this entire block into JobHistory.jsx,
// replacing the existing generateAndDownloadPDF function.
// ─────────────────────────────────────────────────────────────────

const generateAndDownloadPDF = async (jobArg) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PW = pdf.internal.pageSize.getWidth();
  const PH = pdf.internal.pageSize.getHeight();
  const M = 12;
  let y = 0;

  const C = {
    purple: [101, 40, 169], pink:   [204, 40, 153],
    blue:   [37, 99, 235],  violet: [124, 58, 237],
    green:  [5, 150, 105],  amber:  [217, 119, 6],
    red:    [220, 38, 38],  dark:   [15, 23, 42],
    mid:    [51, 65, 85],   gray:   [100, 116, 139],
    silver: [203, 213, 225],snow:   [248, 250, 252],
    white:  [255, 255, 255],
  };

  const isMaint = (j) => j?.job_type?.toLowerCase() === "maintenance";

  const newPage = (need = 40) => {
    if (y + need > PH - 18) { pdf.addPage(); y = M; return true; }
    return false;
  };

  const truncate = (text, maxW, fontSize) => {
    pdf.setFontSize(fontSize);
    const words = (text || "").split(" ");
    let line = "";
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (pdf.getTextWidth(test) > maxW) return line + "...";
      line = test;
    }
    return line;
  };

  // ── Image pipeline ──────────────────────────────────────────────
  // Images are the only heavy part of the PDF. We (a) load them all in
  // PARALLEL up-front via a memoised cache, and (b) downscale each one to the
  // size it is actually placed at — cutting both generation time and file size.
  const imgCache = new Map(); // normalised url -> Promise<HTMLImageElement|null>

  const normUrl = (url) => {
    let u = (url || "").replace(/["\[\]]/g, "").trim();
    if (u && !u.startsWith("http"))
      u = `https://app.sowashusa.com/uploads/job-photos/${u}`;
    return u;
  };

  // Load + fully decode an image, memoised so prefetch and draw share one
  // request. Returns null on failure/timeout so we skip it rather than feeding
  // a half-loaded bitmap into jsPDF (a partial bitmap renders as TV-static).
  const loadImg = (url) => {
    const u = normUrl(url);
    if (!u) return Promise.resolve(null);
    if (imgCache.has(u)) return imgCache.get(u);
    const p = (async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const loaded = new Promise((res) => {
          img.onload = () => res(true);
          img.onerror = () => res(false);
          setTimeout(() => res(false), 12000);
        });
        img.src = u;
        if (!(await loaded) || !img.complete || !img.naturalWidth) return null;
        try { if (img.decode) await img.decode(); } catch {}
        return img;
      } catch { return null; }
    })();
    imgCache.set(u, p);
    return p;
  };

  // Warm the cache with a BOUNDED worker pool (not Promise.all). Firing every
  // request at once arms each image's load-timeout while the browser still has
  // it queued behind the ~6-per-host connection cap, so later images time out
  // and go missing. A pool of 6 means a timer only starts once that image is
  // actually being fetched.
  const prefetchImgs = async (urls, concurrency = 6) => {
    const list = (urls || []).filter(Boolean);
    let next = 0;
    const worker = async () => {
      while (next < list.length) {
        await loadImg(list[next++]);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(concurrency, list.length) }, worker)
    );
  };

  // Re-encode a decoded image to a CLEAN baseline-JPEG data URL via canvas,
  // downscaled to ~200 DPI for its placement width (mm) and never upscaled.
  // This both fixes the old "TV-static" corruption (jsPDF embeds a real JPEG /
  // DCTDecode stream) and keeps the file small. White-fill first so transparent
  // PNGs don't come out as black blocks.
  const imgToJpeg = (img, placeWmm) => {
    const targetPx = placeWmm ? Math.round((placeWmm / 25.4) * 200) : img.naturalWidth;
    const scale = Math.min(1, targetPx / img.naturalWidth);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.72);
  };

  let imgAlias = 0; // unique alias per image -> avoids jsPDF cache collisions
  const drawImg = async (url, x, yy, w, h) => {
    const img = await loadImg(url);
    if (!img) return;
    try {
      pdf.addImage(imgToJpeg(img, w), "JPEG", x, yy, w, h, `img_${imgAlias++}`, "FAST");
    } catch {}
  };

  // Build the exact full URL that drawImg receives, so prefetch keys match.
  const buildFullUrl = (entry) => {
    const u = String(entry.url).replace(/["\[\]]/g, "").trim();
    // An already-absolute path carries its own folder — commercial jobs store
    // photos under /uploads/commercial-sld-photos, which entry.basePath never
    // covers (it is only ever job-photos or maintenance). Stripping the folder
    // and re-prefixing basePath produced a URL that returns index.html with a
    // 200, so the failure was invisible and jsPDF drew garbage.
    if (u.startsWith("http")) return u;
    if (u.startsWith("/")) return `https://app.sowashusa.com${u}`;
    return `${entry.basePath}/${u.split("/").pop()}`;
  };

  const parsePhotos = (raw) => {
    try { return typeof raw === "string" ? JSON.parse(raw) : (raw || []); }
    catch { return []; }
  };

  const fmtTime = (ts) => ts ? format(new Date(ts), "hh:mm a") : "—";
  const fmtDate = (ts) => ts ? format(new Date(ts), "MMM dd, yyyy") : "—";

  // ── FIND COMPANION ─────────────────────────────────────────────
  //
  // STEP 1 — jobArg already carries full detail when opened from the modal or
  //           the bulk loop, so only re-fetch when team_lead_name is missing.
  let mainFull = jobArg;
  if (!mainFull.team_lead_name) {
    try {
      const r = await axios.get(`/api/fo-schedules/${jobArg.schedule_id}/detail`, config);
      mainFull = r.data.job;
    } catch { /* fallback to jobArg */ }
  }

  const selDateStr = format(new Date(mainFull.scheduled_date), "yyyy-MM-dd");

  // STEP 2 — find all candidate jobs from jobs state that share
  //           same date + same client + same site + different id + completed
const candidates = jobs.filter(
  (j) =>
    j.status === "completed" &&
    j.schedule_id !== mainFull.schedule_id &&
    format(new Date(j.scheduled_date), "yyyy-MM-dd") === selDateStr &&
    j.client_name === mainFull.client_name
    // site match NAHI — same team alag sites pe bhi kaam kar sakti hai
);

  // STEP 3 — fetch all candidate details in PARALLEL, then pick the first that
  //           shares the same team lead (Promise.all preserves candidate order).
  let companionFull = null;
  if (candidates.length && mainFull.team_lead_name) {
    const details = await Promise.all(
      candidates.map((candidate) =>
        axios
          .get(`/api/fo-schedules/${candidate.schedule_id}/detail`, config)
          .then((r) => r.data.job)
          .catch(() => null)
      )
    );
    companionFull =
      details.find((cd) => cd && cd.team_lead_name === mainFull.team_lead_name) || null;
  }

  // ── ORDER: earlier arrival = Morning (left), later = Evening (right) ──
  const getArrivalMs = (job) =>
    new Date(job.reached_location_at || job.started_at || job.scheduled_date).getTime();

  let jobL = mainFull;
  let jobR = companionFull;
  if (companionFull && getArrivalMs(companionFull) < getArrivalMs(mainFull)) {
    jobL = companionFull;
    jobR = mainFull;
  }

  const isDual = !!jobR;
  const CW = isDual ? (PW - 2 * M - 5) / 2 : PW - 2 * M;
  const colJobs = isDual ? [jobL, jobR] : [jobL];
  const colColors = [
    { accent: C.blue,   bg: [239, 246, 255], border: [147, 197, 253] },
    { accent: C.violet, bg: [245, 243, 255], border: [196, 181, 253] },
  ];

  // ══════════════════════════════════════════════════════════════════
  // HEADER
  // ══════════════════════════════════════════════════════════════════
  const HH = 62;
  for (let i = 0; i < HH; i++) {
    const t = i / HH;
    pdf.setFillColor(
      Math.round(C.purple[0] + (C.pink[0] - C.purple[0]) * t),
      Math.round(C.purple[1] + (C.pink[1] - C.purple[1]) * t),
      Math.round(C.purple[2] + (C.pink[2] - C.purple[2]) * t)
    );
    pdf.rect(0, i, PW, 1, "F");
  }

  try {
    const logo = new Image(); logo.src = "/sowash-logo.png";
    pdf.addImage(logo, "PNG", PW / 2 - 20, 3, 40, 10);
  } catch {}

  pdf.setTextColor(...C.white);
  pdf.setFontSize(isDual ? 17 : 19);
  pdf.setFont("helvetica", "bold");
  pdf.text(isDual ? "Daily Soiling Report" : "Soiling Report", PW / 2, 22, { align: "center" });

  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    `${jobL.client_name || ""} · ${fmtDate(jobL.scheduled_date)} · Generated ${format(new Date(), "MMM dd, yyyy")}`,
    PW / 2, 30, { align: "center" }
  );

  // KPI strip
  const kpis = isDual
    ? [
        { v: String(jobL.total_panels_cleaned || "—"), l: "MORNING PANELS" },
        { v: String(jobR.total_panels_cleaned || "—"), l: "EVENING PANELS" },
        { v: String(parseInt(jobL.total_panels_cleaned || 0) + parseInt(jobR.total_panels_cleaned || 0)), l: "TOTAL PANELS" },
        { v: "2", l: "SESSIONS" },
      ]
    : [
        { v: String(jobL.total_panels_cleaned || "—"), l: "PANELS CLEANED" },
        { v: `${jobL.system_size || "—"} kW`,          l: "SYSTEM SIZE" },
        { v: jobL.team_lead_name || "N/A",             l: "TEAM LEAD" },
        { v: "COMPLETED",                               l: "STATUS" },
      ];

  const kpiW = (PW - 2 * M) / 4;
  const kpiY = 36;
  kpis.forEach(({ v, l }, i) => {
    const x = M + i * kpiW;
    if (i > 0) {
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.4);
      pdf.line(x, kpiY, x, kpiY + 22);
    }
    pdf.setTextColor(...C.white);
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "bold");
    pdf.text(truncate(v, kpiW - 4, 13), x + kpiW / 2, kpiY + 9, { align: "center" });
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "normal");
    pdf.text(l, x + kpiW / 2, kpiY + 16, { align: "center" });
  });

  y = HH + 5;

  // ══════════════════════════════════════════════════════════════════
  // SHARED TEAM INFO BAR
  // ══════════════════════════════════════════════════════════════════
  const INFO_H = 20;
  pdf.setFillColor(...C.snow);
  pdf.roundedRect(M, y, PW - 2 * M, INFO_H, 2, 2, "F");
  pdf.setDrawColor(...C.silver);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(M, y, PW - 2 * M, INFO_H, 2, 2, "S");

  const infoItems = [
    { l: "TEAM LEAD", v: jobL.team_lead_name || "N/A" },
    { l: "CONTACT",   v: jobL.contact_person || "N/A" },
    { l: "PHONE",     v: jobL.contact_number || "N/A" },
    { l: "LOCATION",  v: jobL.address        || "N/A" },
    { l: "SYSTEM",    v: `${jobL.system_size || "—"} kW` },
  ];

  const infoColW = (PW - 2 * M) / infoItems.length;
  infoItems.forEach(({ l, v }, i) => {
    const x = M + i * infoColW + 4;
    if (i > 0) {
      pdf.setDrawColor(...C.silver);
      pdf.setLineWidth(0.3);
      pdf.line(M + i * infoColW, y + 3, M + i * infoColW, y + INFO_H - 3);
    }
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.gray);
    pdf.text(l, x, y + 7);
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.dark);
    pdf.text(truncate(v, infoColW - 6, 7.5), x, y + 15);
  });

  if (isDual) {
    const badgeX = PW - M - 38;
    pdf.setFillColor(...C.purple);
    pdf.roundedRect(badgeX, y + 5, 36, 10, 2, 2, "F");
    pdf.setTextColor(...C.white);
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "bold");
    pdf.text("SHARED TEAM - BOTH SESSIONS", badgeX + 18, y + 11.5, { align: "center" });
  }

  y += INFO_H + 6;

  // ══════════════════════════════════════════════════════════════════
  // COLUMN HEADERS
  // ══════════════════════════════════════════════════════════════════
  colJobs.forEach((job, ci) => {
    const cc  = colColors[ci];
    const x   = M + ci * (CW + 5);
    const lbl = isDual ? (ci === 0 ? "Morning Session" : "Evening Session") : "Site Details";

    pdf.setFillColor(...cc.bg);
    pdf.roundedRect(x, y, CW, 11, 2, 2, "F");
    pdf.setDrawColor(...cc.border);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(x, y, CW, 11, 2, 2, "S");

    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...cc.accent);
    pdf.text(lbl, x + 4, y + 7.5);

    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.mid);
    pdf.text(truncate(job.site_name || "", CW - 40, 7), x + CW - 3, y + 7.5, { align: "right" });
  });
  y += 14;

  // ══════════════════════════════════════════════════════════════════
  // TIMELINE — side by side
  // ══════════════════════════════════════════════════════════════════
  const tlEvents = [
    { label: "Scheduled",    keys: ["scheduled_date"],                     color: C.blue,        isDate: true  },
    { label: "Site Arrival", keys: ["reached_location_at"],                color: C.amber,       isDate: false },
    { label: "Work Started", keys: ["started_at"],                         color: C.green,       isDate: false },
    { label: "Work Begun",   keys: ["before_photos_at","before_photo_at"], color: C.violet,      isDate: false },
    { label: "Work Done",    keys: ["after_photos_at","after_photo_at"],   color: [204,40,153],  isDate: false },
    { label: "Site Exit",    keys: ["completed_at"],                       color: C.green,       isDate: false },
  ];

  colJobs.forEach((_, ci) => {
    const x = M + ci * (CW + 5);
    pdf.setFontSize(6.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.gray);
    pdf.text("JOB TIMELINE", x + 2, y + 4);
  });
  y += 7;

  const ROW_H = 8;
  const maxTlRows = Math.max(...colJobs.map((job) =>
    tlEvents.filter((ev) => ev.keys.some((k) => job[k])).length
  ));

  colJobs.forEach((job, ci) => {
    const x  = M + ci * (CW + 5);
    let   ry = y;
    const evs = tlEvents.filter((ev) => ev.keys.some((k) => job[k]));

    evs.forEach((ev, idx) => {
      if (idx % 2 === 0) {
        pdf.setFillColor(...C.snow);
        pdf.rect(x, ry, CW, ROW_H, "F");
      }
      pdf.setFillColor(...ev.color);
      pdf.circle(x + 4, ry + ROW_H / 2, 1.8, "F");

      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...ev.color);
      pdf.text(ev.label, x + 8, ry + ROW_H / 2 + 1);

      const ts  = ev.keys.map((k) => job[k]).find(Boolean);
      const val = ev.isDate ? fmtDate(ts) : fmtTime(ts);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...C.dark);
      pdf.text(val, x + CW - 2, ry + ROW_H / 2 + 1, { align: "right" });
      ry += ROW_H;
    });
  });
  y += maxTlRows * ROW_H + 8;

  // ══════════════════════════════════════════════════════════════════
  // FSR DETAILS — side by side
  // ══════════════════════════════════════════════════════════════════
  if (colJobs.some((j) => !isMaint(j) && j.status === "completed")) {
    newPage(55);

    colJobs.forEach((_, ci) => {
      const x = M + ci * (CW + 5);
      pdf.setFontSize(6.5);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...C.gray);
      pdf.text("FIELD SERVICE REPORT", x + 2, y + 4);
    });
    y += 7;

    const fsrRows = [
      { label: "Cable Condition",   key: "cable_condition" },
      { label: "Panel Damage",      key: "panel_damage" },
      { label: "Inverter Alarm",    key: "inverter_alarm" },
      { label: "Potential Shading", key: "potential_shading" },
      { label: "Total Panels",      key: "total_panels_cleaned" },
    ];

    const FSR_H = 8;
    colJobs.forEach((job, ci) => {
      const x  = M + ci * (CW + 5);
      let   fy = y;
      fsrRows.forEach((f, idx) => {
        const raw    = (job[f.key] || "N/A").toString();
        const isGood = ["good", "no"].includes(raw.toLowerCase());
        const isBad  = ["yes", "bad"].includes(raw.toLowerCase());
        const valCol = isBad ? C.red : isGood ? C.green : C.dark;

        if (idx % 2 === 0) {
          pdf.setFillColor(...C.snow);
          pdf.rect(x, fy, CW, FSR_H, "F");
        }
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...C.gray);
        pdf.text(f.label, x + 3, fy + FSR_H / 2 + 1);

        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...valCol);
        pdf.text(truncate(raw, CW / 2 - 4, 7), x + CW - 3, fy + FSR_H / 2 + 1, { align: "right" });
        fy += FSR_H;
      });
    });
    y += fsrRows.length * FSR_H + 8;
  }

  // ══════════════════════════════════════════════════════════════════
  // SIGNATURES
  // ══════════════════════════════════════════════════════════════════
  const sigJobs = colJobs.filter((j) => j.customer_signature);
  if (sigJobs.length > 0) {
    newPage(30);
    pdf.setFontSize(6.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.gray);
    pdf.text("CUSTOMER SIGNATURE", M + 2, y + 4);
    y += 7;
    for (const [ci, job] of sigJobs.entries()) {
      const x = isDual ? M + ci * (CW + 5) : M;
      const sigUrl = job.customer_signature.startsWith("http")
        ? job.customer_signature
        : `https://app.sowashusa.com/uploads/signatures/${job.customer_signature}`;
      await drawImg(sigUrl, x, y, CW * 0.6, 20);
    }
    y += 26;
  }

  // ══════════════════════════════════════════════════════════════════
  // PHOTOS
  // ══════════════════════════════════════════════════════════════════
  const PHOTO_COLS = 4;
  const GAP        = 3;
  const IMG_W      = (PW - 2 * M - GAP * (PHOTO_COLS - 1)) / PHOTO_COLS;
  const IMG_H      = IMG_W * 0.72;

  const drawPhotoGroup = async (groupLabel, accentColor, photoEntries) => {
    if (!photoEntries.length) return;
    newPage(IMG_H + 20);

    pdf.setFillColor(...accentColor);
    pdf.roundedRect(M, y, PW - 2 * M, 8, 2, 2, "F");
    pdf.setTextColor(...C.white);
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "bold");
    pdf.text(groupLabel, M + 4, y + 5.5);
    y += 11;

    for (let i = 0; i < photoEntries.length; i++) {
      const col = i % PHOTO_COLS;
      if (col === 0 && i > 0) newPage(IMG_H + 10);

      const x = M + col * (IMG_W + GAP);

      pdf.setDrawColor(...C.silver);
      pdf.setLineWidth(0.3);
      pdf.rect(x, y, IMG_W, IMG_H);

      await drawImg(buildFullUrl(photoEntries[i]), x + 0.5, y + 0.5, IMG_W - 1, IMG_H - 1);

      pdf.setFontSize(5.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...C.gray);
      pdf.text(
        truncate(photoEntries[i].caption, IMG_W, 5.5),
        x + IMG_W / 2, y + IMG_H + 4, { align: "center" }
      );

      if (col === PHOTO_COLS - 1 || i === photoEntries.length - 1) {
        y += IMG_H + 8;
      }
    }
    y += 3;
  };

  const getEntries = (job, key, fallbackKey, labelPrefix) => {
    const basePath = isMaint(job)
      ? "https://app.sowashusa.com/uploads/maintenance"
      : "https://app.sowashusa.com/uploads/job-photos";
    let photos = parsePhotos(job[key]);
    if (!photos.length && fallbackKey && job[fallbackKey])
      photos = [job[fallbackKey]];
    return photos.map((url, pi) => ({
      url,
      basePath,
      caption: `${labelPrefix} ${pi + 1}`,
    }));
  };

  const photoGroups = isDual
    ? [
        ["MORNING — BEFORE PHOTOS", C.amber,  getEntries(jobL, "before_photos", "before_photo_url", "Before")],
        ["MORNING — AFTER PHOTOS",  C.green,  getEntries(jobL, "after_photos",  "after_photo_url",  "After")],
        ["MORNING — TOOL BOX TALK", C.violet, getEntries(jobL, "temp_voltage_photos", null, "TBT")],
        ["EVENING — BEFORE PHOTOS", C.amber,  getEntries(jobR, "before_photos", "before_photo_url", "Before")],
        ["EVENING — AFTER PHOTOS",  C.green,  getEntries(jobR, "after_photos",  "after_photo_url",  "After")],
        ["EVENING — TOOL BOX TALK", C.violet, getEntries(jobR, "temp_voltage_photos", null, "TBT")],
      ]
    : [
        ["BEFORE PHOTOS",        C.amber,  getEntries(jobL, "before_photos", "before_photo_url", "Before")],
        ["AFTER PHOTOS",         C.green,  getEntries(jobL, "after_photos",  "after_photo_url",  "After")],
        ["TOOL BOX TALK PHOTOS", C.violet, getEntries(jobL, "temp_voltage_photos", null, "TBT")],
      ];

  // Warm every photo in parallel BEFORE drawing, so the draw pass is just
  // cache hits instead of one serial network wait per image.
  await prefetchImgs(photoGroups.flatMap(([, , entries]) => entries.map(buildFullUrl)));

  for (const [label, color, entries] of photoGroups) {
    await drawPhotoGroup(label, color, entries);
  }

  // ══════════════════════════════════════════════════════════════════
  // REMARKS
  // ══════════════════════════════════════════════════════════════════
  colJobs.forEach((job) => {
    const txt = job.remarks || job.notes || job.additional_notes;
    if (!txt) return;
    newPage(22);
    const lines = pdf.splitTextToSize(txt, PW - 2 * M - 8);
    const bH    = lines.length * 5 + 10;
    pdf.setFillColor(239, 246, 255);
    pdf.roundedRect(M, y, PW - 2 * M, bH, 2, 2, "F");
    pdf.setDrawColor(...C.blue);
    pdf.setLineWidth(0.4);
    pdf.roundedRect(M, y, PW - 2 * M, bH, 2, 2, "S");
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.blue);
    pdf.text("REMARKS", M + 3, y + 6);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.dark);
    pdf.text(lines, M + 3, y + 12);
    y += bH + 5;
  });

  // ══════════════════════════════════════════════════════════════════
  // PAGE FOOTERS
  // ══════════════════════════════════════════════════════════════════
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setDrawColor(...C.silver);
    pdf.setLineWidth(0.3);
    pdf.line(M, PH - 10, PW - M, PH - 10);
    pdf.setFontSize(6.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.gray);
    pdf.text("SoWash — Maintenance Services", M, PH - 6);
    pdf.text(`Page ${i} of ${totalPages}`, PW / 2, PH - 6, { align: "center" });
    pdf.text(format(new Date(), "MMM dd, yyyy"), PW - M, PH - 6, { align: "right" });
  }

  pdf.save(
    `Report_${(jobArg.site_name || String(jobArg.schedule_id)).replace(/[^a-zA-Z0-9]/g, "_")}_${selDateStr}.pdf`
  );
};

  // Single-job download (called from modal)
  const handleDownloadReport = async () => {
    setDownloadLoading(true);
    try {
      await generateAndDownloadPDF(selectedJob);
      showToast('success', 'Report downloaded successfully');
    } catch (error) {
      console.error('PDF Error:', error);
      showToast('error', 'Failed to generate report: ' + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  // ── Loading screen ────────────────────────────────────────────
  if (loading && jobs.length === 0)
    return (
      <div style={{ background: T.bg, fontFamily: T.font, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <style>{STYLES}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
          <div style={{ color: T.muted, fontSize: 13 }}>Loading jobs…</div>
        </div>
      </div>
    );

  // ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: "14px 20px" }}>
      <style>{STYLES}</style>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
          zIndex: 99999, display: "flex", alignItems: "center", gap: 10,
          background: T.surface,
          border: `1px solid ${toast.type === "success" ? "rgba(52,211,153,0.30)" : "rgba(248,113,113,0.30)"}`,
          color: toast.type === "success" ? T.green : T.red,
          padding: "13px 22px", borderRadius: 12,
          boxShadow: "0 18px 50px rgba(0,0,0,0.55)", fontSize: 13, fontWeight: 700,
          minWidth: 260, maxWidth: "90vw", animation: "toastIn 0.25s ease both",
        }}>
          <span style={{ fontSize: 17 }}>{toast.type === "success" ? "✅" : "❌"}</span>
          {toast.message}
        </div>
      )}

      {/* Uploading overlay */}
      {uploading && (
        <Overlay zIndex={99998}>
          <div style={{
            background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 16,
            padding: "30px 38px", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 14, boxShadow: "0 28px 80px rgba(0,0,0,0.70)",
          }}>
            <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>Uploading photo…</span>
          </div>
        </Overlay>
      )}

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: "hidden" }}>
        <div style={{ padding: "18px 24px" }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 5 }}>NOVAA · CI ADMIN</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--nd-text-strong)", letterSpacing: "-0.03em", lineHeight: 1 }}>Job History &amp; Analytics</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Track cleaning jobs, maintenance services, and performance metrics</div>
        </div>
        <div style={{ height: 2, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)" }} />
      </div>

      {/* ── STAT CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 18 }}>
        {[
          { label: "Total Jobs",  value: stats.total,      sub: "All time",  color: T.blue,   Icon: Activity    },
          { label: "Completed",   value: stats.completed,  sub: "Jobs done", color: T.green,  Icon: CheckCircle },
          { label: "In Progress", value: stats.inProgress, sub: "Active",    color: T.amber,  Icon: Clock       },
          { label: "Scheduled",   value: stats.scheduled,  sub: "Upcoming",  color: T.purple, Icon: Calendar    },
        ].map((s, i) => (
          <div key={s.label} className="jh-stat-card" style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: "18px 20px", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            animation: `fadeUp 0.35s ${i * 0.06}s ease both`, position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 0, right: 0, width: 70, height: 70, borderRadius: "0 14px 0 70px", background: `${s.color}0C` }} />
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: `${s.color}18`, border: `1px solid ${s.color}35`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <s.Icon size={12} color={s.color} />
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: "0.12em", textTransform: "uppercase" }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 34, fontWeight: 800, color: s.color, fontFamily: T.mono, letterSpacing: "-1px", lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 18px", marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
            <SearchIcon style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--nd-tx22)", fontSize: 17 }} />
            <input
              type="text"
              className="jh-input"
              placeholder="Search by site, client, or location…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button className={`jh-filter-btn ${filterType === "all"         ? "jh-active-all"   : ""}`} onClick={() => setFilterType("all")}>All Types</button>
            <button className={`jh-filter-btn ${filterType === "commercial"  ? "jh-active-comm"  : ""}`} onClick={() => setFilterType("commercial")}>
              <CleaningIcon style={{ fontSize: 13 }} /> Commercial
            </button>
            <button className={`jh-filter-btn ${filterType === "maintenance" ? "jh-active-maint" : ""}`} onClick={() => setFilterType("maintenance")}>
              <BuildIcon style={{ fontSize: 13 }} /> Maintenance
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
          <button className={`jh-filter-btn ${filterStatus === "all"       ? "jh-active-all"   : ""}`} onClick={() => setFilterStatus("all")}><AssignmentIcon style={{ fontSize: 13 }} /> All Status</button>
          <button className={`jh-filter-btn ${filterStatus === "completed" ? "jh-active-done"  : ""}`} onClick={() => setFilterStatus("completed")}><CheckCircleIcon style={{ fontSize: 13 }} /> Completed</button>
          <button className={`jh-filter-btn ${filterStatus === "started"   ? "jh-active-prog"  : ""}`} onClick={() => setFilterStatus("started")}><ScheduleIcon style={{ fontSize: 13 }} /> In Progress</button>
          <button className={`jh-filter-btn ${filterStatus === "scheduled" ? "jh-active-sched" : ""}`} onClick={() => setFilterStatus("scheduled")}><CalendarIcon style={{ fontSize: 13 }} /> Scheduled</button>
        </div>

        {/* APPROVAL FILTER ROW — only relevant for completed jobs */}
        {(filterStatus === "completed" || filterStatus === "all") && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", borderTop: `1px solid ${T.border}`, paddingTop: 10, marginTop: 10 }}>
            <span className="jh-label" style={{ marginBottom: 0, paddingRight: 4 }}>Approval</span>
            {[
              { key: "all",      label: "All",      cls: "jh-active-all",  icon: null },
              { key: "pending",  label: "Pending",  cls: "jh-active-prog", icon: <PendingIcon style={{ fontSize: 14 }} /> },
              { key: "approved", label: "Approved", cls: "jh-active-done", icon: <ApproveIcon style={{ fontSize: 14 }} /> },
              { key: "rejected", label: "Rejected", cls: "jh-active-rej",  icon: <RejectIcon  style={{ fontSize: 14 }} /> },
            ].map(({ key, label, cls, icon }) => (
              <button
                key={key}
                onClick={() => setFilterApproval(key)}
                className={`jh-filter-btn ${filterApproval === key ? cls : ""}`}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* BROWSE MODE TOGGLE */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: "12px 18px", marginBottom: 18,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <span className="jh-label" style={{ marginBottom: 0 }}>Browse by</span>
        <div className="jh-seg">
          {[["byClient", "🏢", "By Client"], ["byDate", "📅", "By Date"]].map(([mode, emoji, label]) => (
            <button
              key={mode}
              className={`jh-seg-btn ${browseMode === mode ? "jh-seg-on" : ""}`}
              onClick={() => {
                setBrowseMode(mode);
                setSelectedDate(null);
                setBulkDownloadProgress({});
                setBulkDone(false);
                if (mode === "byClient") setViewMode("clients");
              }}
            >
              <span>{emoji}</span> {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── BREADCRUMB + BACK ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        {/* Back button — shown when not at the top level of either mode */}
        {(browseMode === "byClient" ? viewMode !== "clients" : selectedDate !== null) && (
          <button
            onClick={handleBack}
            style={{
              display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
              background: "var(--nd-w04)", border: `1px solid ${T.border}`,
              borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600, color: T.muted,
              fontFamily: T.font, transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "var(--nd-w08)"; e.currentTarget.style.color = T.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = "var(--nd-w04)"; e.currentTarget.style.color = T.muted; }}
          >
            <ArrowBackIcon style={{ fontSize: 15 }} /> Back
          </button>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", fontSize: 12, fontWeight: 600 }}>
          {browseMode === "byDate" ? (
            // By-Date breadcrumb: All Dates / Apr 22, 2026
            <>
              <span
                style={{ color: selectedDate ? T.muted : T.text, cursor: selectedDate ? "pointer" : "default", transition: "color 0.15s" }}
                onClick={() => { if (selectedDate) { setSelectedDate(null); setBulkDownloadProgress({}); setBulkDone(false); } }}
              >
                All Dates
              </span>
              {selectedDate && (
                <>
                  <ChevronRight size={12} color="var(--nd-tx35)" />
                  <span style={{ color: T.text }}>{format(new Date(selectedDate), "MMMM dd, yyyy")}</span>
                </>
              )}
            </>
          ) : (
            // By-Client breadcrumb: All Clients / Client / Month / Date
            <>
              <span
                style={{ color: viewMode === "clients" ? T.text : T.muted, cursor: viewMode !== "clients" ? "pointer" : "default", transition: "color 0.15s" }}
                onClick={() => {
                  if (viewMode !== "clients") {
                    setViewMode("clients"); setSelectedClient(null); setSelectedMonth(null); setSearchQuery("");
                  }
                }}
              >
                All Clients
              </span>
              {viewMode !== "clients" && (
                <>
                  <ChevronRight size={12} color="var(--nd-tx35)" />
                  <span
                    style={{ color: viewMode === "months" ? T.text : T.muted, cursor: viewMode !== "months" ? "pointer" : "default" }}
                    onClick={() => { if (viewMode !== "months") { setViewMode("months"); setSelectedMonth(null); } }}
                  >
                    {selectedClient}
                  </span>
                </>
              )}
              {(viewMode === "dates" || viewMode === "jobs") && (
                <>
                  <ChevronRight size={12} color="var(--nd-tx35)" />
                  <span
                    style={{ color: viewMode === "dates" ? T.text : T.muted, cursor: viewMode === "jobs" ? "pointer" : "default" }}
                    onClick={() => { if (viewMode === "jobs") { setViewMode("dates"); setSelectedDate(null); } }}
                  >
                    {selectedMonth && format(new Date(selectedMonth + "-01"), "MMMM yyyy")}
                  </span>
                </>
              )}
              {browseMode === "byClient" && viewMode === "jobs" && (
                <>
                  <ChevronRight size={12} color="var(--nd-tx35)" />
                  <span style={{ color: T.text }}>{format(new Date(selectedDate), "MMM dd")}</span>
                </>
              )}
            </>
          )}
        </div>
      </div>


      {/* ── VIEW 1: CLIENT CARDS ── */}
      {browseMode === "byClient" && viewMode === "clients" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 14 }}>
          {Object.keys(clientGroups).length > 0 ? (
            Object.keys(clientGroups).map((client, i) => (
              <div key={i} className="jh-nav-card" onClick={() => handleClientClick(client)} style={{ animationDelay: `${i * 0.05}s` }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <BusinessIcon style={{ color: T.blue, fontSize: 22 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{client}</div>
                  <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{clientGroups[client].length} total jobs</div>
                </div>
                <ChevronRight size={16} color="var(--nd-tx35)" />
              </div>
            ))
          ) : (
            <div style={{ gridColumn: "1/-1", textAlign: "center", color: T.muted, padding: "40px 0", fontSize: 13 }}>No clients found.</div>
          )}
        </div>
      )}

      {/* ── VIEW 2: MONTH CARDS ── */}
      {browseMode === "byClient" && viewMode === "months" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 14 }}>
          {Object.keys(monthGroups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((monthKey, i) => (
              <div key={i} className="jh-nav-card" onClick={() => handleMonthClick(monthKey)} style={{ animationDelay: `${i * 0.05}s` }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(167,139,250,0.10)", border: "1px solid rgba(167,139,250,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <EventIcon style={{ color: T.purple, fontSize: 22 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{format(new Date(monthKey + "-01"), "MMMM yyyy")}</div>
                  <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{monthGroups[monthKey].length} jobs</div>
                </div>
                <ChevronRight size={16} color="var(--nd-tx35)" />
              </div>
            ))}
        </div>
      )}

      {/* ── VIEW 3: DATE CARDS ── */}
      {browseMode === "byClient" && viewMode === "dates" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 14 }}>
          {Object.keys(dateGroups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((date, i) => (
              <div key={i} className="jh-nav-card" onClick={() => handleDateClick(date)} style={{ animationDelay: `${i * 0.05}s` }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DateRangeIcon style={{ color: T.amber, fontSize: 22 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{format(new Date(date), "MMMM dd, yyyy")}</div>
                  <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{dateGroups[date].length} jobs · {format(new Date(date), "EEEE")}</div>
                </div>
                <ChevronRight size={16} color="var(--nd-tx35)" />
              </div>
            ))}
        </div>
      )}

      {/* BY-DATE VIEW A: All dates list */}
      {browseMode === "byDate" && !selectedDate && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {Object.keys(allDateGroups).sort((a, b) => new Date(b) - new Date(a)).map((dateStr) => {
            const dayJobs = allDateGroups[dateStr];
            const completed  = dayJobs.filter(j => j.status === "completed").length;
            const inProgress = dayJobs.filter(j => ["started","in_progress","before_photos","after_photos"].includes(j.status)).length;
            const scheduled  = dayJobs.filter(j => j.status === "scheduled").length;
            const uniqueClients = [...new Set(dayJobs.map(j => j.client_name).filter(Boolean))];
            const isToday = dateStr === format(new Date(), "yyyy-MM-dd");
            return (
              <div
                key={dateStr}
                className="jh-nav-card"
                onClick={() => { setSelectedDate(dateStr); setBulkDownloadProgress({}); setBulkDone(false); }}
                style={{ gap: 18, padding: "16px 20px" }}
              >
                {/* Date tile */}
                <div style={{
                  minWidth: 60, borderRadius: 12, padding: "9px", textAlign: "center", flexShrink: 0,
                  background: isToday ? "rgba(59,130,246,0.12)" : "var(--nd-w03)",
                  border: `1px solid ${isToday ? "rgba(59,130,246,0.30)" : T.border}`,
                }}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.10em", color: isToday ? T.blue : T.muted }}>
                    {format(new Date(dateStr), "EEE")}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1.1, fontFamily: T.mono, color: isToday ? T.blue : T.text }}>
                    {format(new Date(dateStr), "dd")}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.muted }}>
                    {format(new Date(dateStr), "MMM yyyy")}
                  </div>
                </div>
                {/* Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                      {dayJobs.length} Job{dayJobs.length !== 1 ? "s" : ""}
                    </span>
                    {isToday && (
                      <span style={{ fontSize: 9, fontWeight: 800, background: "rgba(59,130,246,0.14)", color: T.blue, border: "1px solid rgba(59,130,246,0.30)", padding: "2px 9px", borderRadius: 20, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: T.mono }}>Today</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 7 }}>
                    {completed  > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: T.green, background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.24)", padding: "2px 9px", borderRadius: 20 }}>✓ {completed} Done</span>}
                    {inProgress > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: T.amber, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.24)", padding: "2px 9px", borderRadius: 20 }}>⏳ {inProgress} In Progress</span>}
                    {scheduled  > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: T.blue,  background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.24)", padding: "2px 9px", borderRadius: 20 }}>📋 {scheduled} Scheduled</span>}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {uniqueClients.map(client => (
                      <span key={client} style={{ fontSize: 10, fontWeight: 600, color: T.muted, background: "var(--nd-w04)", border: `1px solid ${T.border}`, padding: "2px 8px", borderRadius: 6 }}>{client}</span>
                    ))}
                  </div>
                </div>
                <ChevronRight size={16} color="var(--nd-tx35)" />
              </div>
            );
          })}
          {Object.keys(allDateGroups).length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: T.muted, fontSize: 13 }}>No jobs found.</div>
          )}
        </div>
      )}

      {/* BY-DATE VIEW B: Jobs for selected date + Download All */}
      {browseMode === "byDate" && selectedDate && (() => {
        const dayJobs = allDateGroups[selectedDate] || [];
        const doneCount  = Object.values(bulkDownloadProgress).filter(v => v === "done").length;
        const totalCount = dayJobs.length;
        return (
          <div>
            {/* Download All Banner */}
            <div style={{
              background: T.surface, borderRadius: 14, padding: "16px 20px", marginBottom: 16,
              border: `1px solid ${bulkDone ? "rgba(52,211,153,0.30)" : T.border}`,
              transition: "border 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>
                    {totalCount} Report{totalCount !== 1 ? "s" : ""} · {format(new Date(selectedDate), "MMMM dd, yyyy")}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                    Each job downloads as a separate PDF file
                  </div>
                </div>
                <button
                  className="jh-btn-primary"
                  onClick={handleDownloadAllReports}
                  disabled={bulkDownloading}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {bulkDownloading ? (
                    <>
                      <span style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTop: `2px solid ${T.blue}`, borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                      Downloading…
                    </>
                  ) : (
                    <><DownloadIcon style={{ fontSize: 16 }} /> Download All Reports</>
                  )}
                </button>
              </div>
              {/* Progress bar */}
              {(bulkDownloading || bulkDone) && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: bulkDone ? T.green : T.blue }}>
                      {bulkDone ? "✅ All reports downloaded!" : `Generating PDF ${doneCount + 1} of ${totalCount}…`}
                    </span>
                    <span style={{ fontSize: 12, color: T.muted, fontWeight: 600, fontFamily: T.mono }}>{doneCount} / {totalCount}</span>
                  </div>
                  <div style={{ height: 6, background: "var(--nd-w05)", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 99, transition: "width 0.4s ease",
                      width: `${totalCount > 0 ? (doneCount / totalCount) * 100 : 0}%`,
                      background: bulkDone ? T.green : "linear-gradient(90deg,#3B82F6,#22D3A5)",
                    }} />
                  </div>
                </div>
              )}
            </div>

            {/* Job cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(360px,1fr))", gap: 18 }}>
              {dayJobs.map((job, index) => {
                const sc   = getStatusCfg(job.status);
                const tc   = getTypeCfg(job.job_type);
                const prog = bulkDownloadProgress[job.schedule_id];
                return (
                  <div
                    key={job.unique_id || job.schedule_id || index}
                    className="jh-job-card"
                    style={{
                      background: T.surface, borderRadius: 18, padding: 22,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                      border: `1px solid ${prog === "done" ? "rgba(52,211,153,0.40)" : tc.border}`,
                      position: "relative", overflow: "hidden",
                      animationDelay: `${index * 0.07}s`, transition: "border 0.3s",
                    }}
                  >
                    <div style={{ position: "absolute", top: 0, right: 0, width: 90, height: 90, borderRadius: "0 18px 0 90px", background: `${tc.color}09` }} />

                    {/* Type badge */}
                    <div style={{
                      position: "absolute", top: 14, right: 14, padding: "3px 9px",
                      background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                      borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: "0.08em", fontFamily: T.mono,
                      display: "flex", alignItems: "center", gap: 4, zIndex: 2,
                    }}>
                      {job.job_type === "maintenance" ? <BuildIcon style={{ fontSize: 11 }} /> : <CleaningIcon style={{ fontSize: 11 }} />}
                      {tc.label}
                    </div>

                    {/* Site + client */}
                    <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 16, paddingRight: 90 }}>
                      <div style={{ width: 46, height: 46, borderRadius: 13, background: sc.bg, border: `1.5px solid ${sc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: sc.color, flexShrink: 0 }}>
                        {(job.site_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 3 }}>{job.site_name}</div>
                        <div style={{ fontSize: 11, color: T.muted }}>{job.client_name}</div>
                      </div>
                    </div>

                    {/* Status */}
                    <div style={{ marginBottom: 12 }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px",
                        background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                        borderRadius: 8, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.mono,
                      }}>
                        {getStatusIcon(job.status)} {job.status?.replace(/_/g, " ")}
                      </span>
                    </div>

                    {/* Approval badge — completed jobs only */}
                    {job.status === "completed" && (() => {
                      const ab = getApprovalBadge(job.approval_status);
                      return (
                        <div style={{ marginBottom: 12 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: ab.color, background: ab.bg, border: `1px solid ${ab.border}`, padding: "3px 11px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 5, fontFamily: T.mono, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                            {ab.icon} {ab.label}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Per-job bulk status indicator */}
                    {prog && (() => {
                      const pc = prog === "done"  ? { c: T.green, bg: "rgba(52,211,153,0.08)",  bd: "rgba(52,211,153,0.24)" }
                               : prog === "error" ? { c: T.red,   bg: "rgba(248,113,113,0.08)", bd: "rgba(248,113,113,0.24)" }
                               :                    { c: T.blue,  bg: "rgba(59,130,246,0.08)",  bd: "rgba(59,130,246,0.24)" };
                      return (
                        <div style={{ marginBottom: 12, padding: "7px 11px", borderRadius: 9, background: pc.bg, border: `1px solid ${pc.bd}`, display: "flex", alignItems: "center", gap: 8 }}>
                          {prog === "downloading" && <span style={{ width: 12, height: 12, border: `2px solid ${T.border}`, borderTop: `2px solid ${T.blue}`, borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />}
                          <span style={{ fontSize: 11, fontWeight: 700, color: pc.c }}>
                            {prog === "pending" ? "⏳ Queued" : prog === "downloading" ? "Generating PDF…" : prog === "done" ? "✅ Downloaded" : "❌ Failed"}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Attendance Count */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                      <PersonIcon style={{ fontSize: 15, color: "var(--nd-tx35)" }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>
                        Attendance: <span style={{ color: T.text, fontFamily: T.mono, fontWeight: 800 }}>{getJobAttendance(job).length}</span>
                      </span>
                    </div>

                    {/* Action buttons */}
                    <button
                      onClick={() => handleViewDetails(job)}
                      style={{
                        width: "100%", padding: 11, border: "none", borderRadius: 11, cursor: "pointer",
                        fontFamily: T.font, fontSize: 13, fontWeight: 700, color: "var(--nd-text-strong)",
                        background: job.job_type === "maintenance"
                          ? "linear-gradient(135deg,#A78BFA,#7C3AED)"
                          : "linear-gradient(135deg,#3B82F6,#22D3A5)",
                        boxShadow: job.job_type === "maintenance"
                          ? "0 4px 14px rgba(167,139,250,0.3)"
                          : "0 4px 14px rgba(59,130,246,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                        transition: "opacity 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                    >
                      <ViewIcon style={{ fontSize: 17 }} /> View Details
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── VIEW 4: JOB CARDS ── */}
      {viewMode === "jobs" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(360px,1fr))", gap: 18 }}>
          {currentJobsList.length > 0 ? (
            currentJobsList.map((job, index) => {
              const sc = getStatusCfg(job.status);
              const tc = getTypeCfg(job.job_type);
              return (
                <div
                  key={job.unique_id || job.schedule_id || index}
                  className="jh-job-card"
                  style={{
                    background: T.surface,
                    border: `1px solid ${tc.border}`,
                    borderRadius: 18,
                    padding: 22,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    position: "relative",
                    overflow: "hidden",
                    animationDelay: `${index * 0.07}s`,
                  }}
                >
                  <div style={{ position: "absolute", top: 0, right: 0, width: 90, height: 90, borderRadius: "0 18px 0 90px", background: `${tc.color}09` }} />

                  <div style={{
                    position: "absolute", top: 14, right: 14, padding: "3px 9px",
                    background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                    borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.08em", fontFamily: T.mono,
                    display: "flex", alignItems: "center", gap: 4, zIndex: 2,
                  }}>
                    {job.job_type === "maintenance" ? <BuildIcon style={{ fontSize: 11 }} /> : <CleaningIcon style={{ fontSize: 11 }} />}
                    {tc.label}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 16, paddingRight: 90 }}>
                    <div style={{
                      width: 46, height: 46, borderRadius: 13, background: sc.bg, border: `1.5px solid ${sc.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 18, fontWeight: 800, color: sc.color, flexShrink: 0,
                    }}>
                      {(job.site_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 3 }}>{job.site_name}</div>
                      <div style={{ fontSize: 11, color: T.muted }}>{job.client_name}</div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px",
                      background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                      borderRadius: 8, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: "0.08em", fontFamily: T.mono,
                    }}>
                      {getStatusIcon(job.status)} {job.status.replace("_", " ")}
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                    {job.address && (
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <LocationIcon style={{ color: T.blue, fontSize: 14 }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="jh-label" style={{ marginBottom: 1 }}>Location</div>
                          <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{job.location || job.address}</div>
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <CalendarIcon style={{ color: T.amber, fontSize: 14 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="jh-label" style={{ marginBottom: 1 }}>Scheduled</div>
                        <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{format(new Date(job.scheduled_date), "MMM dd, yyyy")}</div>
                      </div>
                    </div>
                  </div>

                  {/* Task/System and Service Number Grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 12, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 12px", textAlign: "center" }}>
                      <div className="jh-label" style={{ marginBottom: 3 }}>
                        {job.job_type === "maintenance" ? "Task" : "System"}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                        {job.job_type === "maintenance" ? (job.task_name || "Maint.") : `${job.system_size} kW`}
                      </div>
                    </div>

                    <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 12px", textAlign: "center" }}>
                      <div className="jh-label" style={{ marginBottom: 3 }}>Service</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: T.mono }}>#{job.service_number}</div>
                    </div>
                  </div>

                  {/* Efficiency Timer / Result Section */}
                  <div style={{ marginBottom: 14 }}>
                    <JobEfficiencyTimer job={job} />
                  </div>

                  {/* Approval badge — completed jobs only */}
                  {job.status === "completed" && (() => {
                    const ab = getApprovalBadge(job.approval_status);
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "9px 12px", borderRadius: 10, background: ab.bg, border: `1px solid ${ab.border}`, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: ab.color, display: "flex", alignItems: "center", gap: 6 }}>
                          {ab.icon} Client Approval: {ab.label}
                        </span>
                        {job.rejection_reason && (
                          <span style={{ fontSize: 11, color: T.red, marginLeft: "auto", fontStyle: "italic" }}>
                            "{job.rejection_reason}"
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  <button
                    onClick={() => handleViewDetails(job)}
                    style={{
                      width: "100%", padding: 11, border: "none", borderRadius: 11, cursor: "pointer",
                      fontFamily: T.font, fontSize: 13, fontWeight: 700, color: "var(--nd-text-strong)",
                      background: job.job_type === "maintenance"
                        ? "linear-gradient(135deg,#A78BFA,#7C3AED)"
                        : "linear-gradient(135deg,#3B82F6,#22D3A5)",
                      boxShadow: job.job_type === "maintenance"
                        ? "0 4px 14px rgba(167,139,250,0.3)"
                        : "0 4px 14px rgba(59,130,246,0.3)",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                  >
                    <ViewIcon style={{ fontSize: 17 }} /> View Details
                  </button>

                  {/* Attendance Count */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 11, color: T.muted, marginTop: 12, fontWeight: 600 }}>
                    <PersonIcon style={{ fontSize: 14 }} /> Attendance:{" "}
                    <span style={{ color: T.text, fontFamily: T.mono, fontWeight: 800 }}>{getJobAttendance(job).length}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "40px 0", color: T.muted, fontSize: 13 }}>
              No jobs found for this date.
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════
          JOB DETAILS MODAL
      ══════════════════════════════════════════ */}
      {detailsOpen && (
        <Overlay onClick={() => { setIsEditing(false); setDetailsOpen(false); }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 20,
            maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "hidden",
            boxShadow: "0 28px 80px rgba(0,0,0,0.70)", animation: "modalIn 0.2s ease both",
            display: "flex", flexDirection: "column",
          }}>

            {/* Modal header */}
            <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", background: "var(--nd-w02)", flexShrink: 0 }}>
              <div style={{ flex: 1, marginRight: 12 }}>
                {selectedJob && (
                  <>
                    <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 4 }}>
                      {selectedJob.job_type === "maintenance" ? "MAINTENANCE SERVICE" : "COMMERCIAL CLEANING"}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--nd-text-strong)", letterSpacing: "-0.02em", marginBottom: 6 }}>{selectedJob.site_name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {(() => { const sc = getStatusCfg(selectedJob.status); return (
                        <span style={{ padding: "3px 9px", background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.mono }}>
                          {getStatusIcon(selectedJob.status)} {selectedJob.status.replace("_", " ")}
                        </span>
                      ); })()}
                      {selectedJob.status === "completed" && (() => {
                        const ab = getApprovalBadge(selectedJob.approval_status);
                        return (
                          <span style={{ padding: "3px 9px", background: ab.bg, color: ab.color, border: `1px solid ${ab.border}`, borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.mono }}>
                            {ab.icon} {ab.label}
                          </span>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {selectedJob && !isEditing && !detailsLoading && (
                  <button
                    className="jh-btn-ghost"
                    style={{ padding: "7px 14px", fontSize: 12 }}
                    onClick={() => { setEditData({ ...selectedJob }); setIsEditing(true); }}
                  >
                    Edit Job
                  </button>
                )}
                <CloseBtn onClick={() => { setIsEditing(false); setDetailsOpen(false); }} />
              </div>
            </div>
            <div style={{ height: 2, background: selectedJob?.job_type === "maintenance" ? "linear-gradient(90deg,#A78BFA,#7C3AED,transparent)" : "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)", flexShrink: 0 }} />

            {detailsLoading ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
                <div style={{ color: T.muted, fontSize: 13 }}>Loading details…</div>
              </div>
            ) : (
            selectedJob && (
              <>
                <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px" }}>
                  {/* SITE INFORMATION */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                    <SecHead icon={LocationIcon} label="Site Information" color={T.blue} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <FieldCell label="Client"   value={selectedJob.client_name} />
                      <FieldCell label="Job Type" value={selectedJob.job_type === "maintenance" ? "Maintenance Service" : "Commercial Cleaning"} />
                      {selectedJob.system_size > 0 && <FieldCell label="System Size" value={`${selectedJob.system_size} kW`} />}
                      <FieldCell label="Service Number" value={`#${selectedJob.service_number}`} />
                      {selectedJob.contact_person && <FieldCell label="Contact Person" value={selectedJob.contact_person} />}
                      {selectedJob.contact_number && <FieldCell label="Contact Number" value={selectedJob.contact_number} />}
                      {selectedJob.team_lead_name  && <FieldCell label="Team Lead"      value={selectedJob.team_lead_name} />}
                      {selectedJob.address && (
                        <FieldCell
                          label="Address"
                          value={`${selectedJob.address}, ${selectedJob.city}${selectedJob.state ? ", " + selectedJob.state : ""}`}
                          span
                        />
                      )}
                    </div>
                  </div>

                  {/* Attendance Details */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                    <SecHead icon={PersonIcon} label="Attendance Records" color={T.green} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                      {(() => {
                        const attList = getJobAttendance(selectedJob);
                        if (attList.length === 0) {
                          return <div style={{ fontSize: 12, color: T.muted }}>No attendance logged for this job.</div>;
                        }
                        return attList.map((att, idx) => (
                          <div key={idx} className="jh-field-cell" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{att.fo_name || "Unknown"}</div>
                              <div className="jh-label" style={{ marginTop: 4, marginBottom: 0 }}>Status: {att.status || "—"}</div>
                            </div>
                            <div style={{ textAlign: "right", fontFamily: T.mono }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>In: <span style={{ color: T.text }}>{att.clock_in_at ? format(new Date(att.clock_in_at), "hh:mm a") : "—"}</span></div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginTop: 3 }}>Out: <span style={{ color: T.text }}>{att.clock_out_at ? format(new Date(att.clock_out_at), "hh:mm a") : "—"}</span></div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Job Timeline */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                    <SecHead icon={TimeIcon} label="Job Timeline" color={T.blue} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <div className="jh-timeline-dot" style={{ background: "rgba(59,130,246,0.12)", border: "1.5px solid rgba(59,130,246,0.30)" }}>
                          <CalendarIcon style={{ color: T.blue, fontSize: 15 }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, marginBottom: 3 }}>Scheduled</div>
                          {isEditing ? (
                            <input
                              type="date"
                              className="jh-input"
                              value={formatDateForInput(editData?.scheduled_date)}
                              onChange={(e) => setEditData({ ...editData, scheduled_date: e.target.value })}
                              style={{ maxWidth: 220, padding: "6px 9px", fontSize: 12 }}
                            />
                          ) : (
                            <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                              {format(new Date(selectedJob.scheduled_date), "MMM dd, yyyy")}
                            </div>
                          )}
                        </div>
                      </div>

                      {[
                        { label: "Site Arrival",      key: "reached_location_at",                              color: T.amber,  IconC: LocationIcon    },
                        { label: "Work Area Reached", key: "started_at",                                       color: T.green,  IconC: ScheduleIcon    },
                        { label: "Work Started",      key: "before_photos_at", fallbackKey: "before_photo_at", color: T.purple, IconC: PhotoIcon       },
                        { label: "Work Completed",    key: "after_photos_at",  fallbackKey: "after_photo_at",  color: T.pink,   IconC: PhotoIcon       },
                        { label: "Site Exit",         key: "completed_at",                                     color: T.green,  IconC: CheckCircleIcon },
                      ].map((item) => {
                        const timestamp = selectedJob[item.key] || selectedJob[item.fallbackKey];
                        const editTimestamp = editData?.[item.key] || editData?.[item.fallbackKey];

                        if (!timestamp && !isEditing) return null;

                        return (
                          <div key={item.key} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <div className="jh-timeline-dot" style={{ background: `${item.color}12`, border: `1.5px solid ${item.color}30` }}>
                              <item.IconC style={{ color: item.color, fontSize: 15 }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: item.color, marginBottom: 3 }}>{item.label}</div>
                              {isEditing ? (
                                <input
                                  type="datetime-local"
                                  className="jh-input"
                                  value={formatDateTimeForInput(editTimestamp)}
                                  onChange={(e) => setEditData({ ...editData, [item.key]: e.target.value })}
                                  style={{ maxWidth: 260, padding: "6px 9px", fontSize: 12 }}
                                />
                              ) : (
                                <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                                  {timestamp ? format(new Date(timestamp), "MMM dd, yyyy • hh:mm a") : "Pending"}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photos */}
                  {(selectedJob.before_photos || selectedJob.after_photos || selectedJob.temp_voltage_photos || selectedJob.before_photo_url || selectedJob.after_photo_url || isEditing) && (
                    <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                      <SecHead icon={PhotoIcon} label="Photo Gallery" color={T.purple} />
                      {[
                        { key: 'before_photos',       fallbackKey: 'before_photo_url', label: '📷 Before Photos' },
                        { key: 'after_photos',        fallbackKey: 'after_photo_url',  label: '📷 After Photos' },
                        { key: 'temp_voltage_photos', fallbackKey: null,               label: '⚡ Tool Box Talk' },
                      ].map(({ key, fallbackKey, label }) => {
                        const targetData = isEditing ? editData : selectedJob;
                        let raw = targetData?.[key];
                        
                        let photos = [];
                        const parseRaw = (val) => {
                          if (!val) return [];
                          let parsed = val;
                          try { if (typeof parsed === 'string') parsed = JSON.parse(parsed); } catch(e) {}
                          try { if (typeof parsed === 'string') parsed = JSON.parse(parsed); } catch(e) {} // Double parse safety
                          if (Array.isArray(parsed)) return parsed;
                          if (typeof parsed === 'string') return parsed.split(',').map(s => s.trim()).filter(Boolean);
                          return [parsed];
                        };

                        photos = parseRaw(raw);
                        if (photos.length === 0 && fallbackKey && targetData?.[fallbackKey]) {
                          photos = parseRaw(targetData[fallbackKey]);
                        }
                        photos = photos.filter(Boolean);

                        if (photos.length === 0 && !isEditing) return null;

                        return (
                          <div key={key} style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 10 }}>
                              {label}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10 }}>
                              {photos.map((photo, index) => (
                                <AutoFallbackImage
                                  key={`${key}-${index}`}
                                  photoRaw={photo}
                                  alt={`${label} ${index + 1}`}
                                  isEditing={isEditing}
                                  customStyle={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, cursor: isEditing ? "default" : "pointer", border: `1px solid ${T.border}`, background: "var(--nd-w03)" }}
                                  onDelete={() => {
                                    const updated = photos.filter((_, i) => i !== index);
                                    handlePhotoDelete(key, updated);
                                  }}
                                />
                              ))}
                              {isEditing && (
                                <label style={{
                                  height: 120, border: "1px dashed rgba(59,130,246,0.45)", borderRadius: 10,
                                  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                                  color: T.blue, fontSize: 11, fontWeight: 700, flexDirection: "column", gap: 4,
                                  background: "rgba(59,130,246,0.05)", transition: "background 0.15s",
                                }}
                                  onMouseEnter={e => e.currentTarget.style.background = "rgba(59,130,246,0.10)"}
                                  onMouseLeave={e => e.currentTarget.style.background = "rgba(59,130,246,0.05)"}
                                >
                                  + Add Photo
                                  <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        handleImageUpload(e.target.files[0], key);
                                      }
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* CONDITIONAL REPORT SECTION */}
                  {selectedJob.status === "completed" &&
                    (selectedJob.job_type === "maintenance" ? (
                      // MAINTENANCE REPORT
                      <div style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.22)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                        <SecHead icon={EngineeringIcon} label="Maintenance Report" color={T.purple} />

                        <div style={{ marginBottom: 14 }}>
                          <div className="jh-label">Task Name</div>
                          {isEditing ? (
                            <input
                              type="text"
                              className="jh-input"
                              value={editData?.task_name || ""}
                              onChange={(e) => setEditData({ ...editData, task_name: e.target.value })}
                            />
                          ) : (
                            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                              {selectedJob.task_name || "General Maintenance"}
                            </div>
                          )}
                        </div>

                        {(selectedJob.task_description || selectedJob.maintenance_notes || isEditing) && (
                          <div style={{ marginBottom: 14 }}>
                            <div className="jh-label">Description &amp; Notes</div>
                            {isEditing ? (
                              <textarea
                                className="jh-input"
                                value={editData?.task_description || editData?.maintenance_notes || ""}
                                onChange={(e) => setEditData({ ...editData, task_description: e.target.value })}
                                rows={4}
                                style={{ resize: "vertical" }}
                              />
                            ) : (
                              <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                                {selectedJob.task_description || selectedJob.maintenance_notes}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Static Checks for Maintenance Visualization */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                          {[["Safety Check", "PASSED"], ["System Status", "VERIFIED"]].map(([lbl, val]) => (
                            <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.20)", borderRadius: 9, padding: "9px 12px" }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>{lbl}</span>
                              <span style={{ fontSize: 10, fontWeight: 800, color: T.green, fontFamily: T.mono, background: "rgba(52,211,153,0.12)", padding: "2px 7px", borderRadius: 5 }}>{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      // COMMERCIAL FSR
                      <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                        <SecHead icon={DescriptionIcon} label="Field Service Report" color={T.blue} />

                        {/* Main Fields Grid */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                          {[
                            { key: "cable_condition",      label: "Cable Condition",      type: "select", options: ["Good", "Bad"] },
                            { key: "cable_quantity",       label: "Cable Quantity",       type: "text",   placeholder: "e.g. 10 meters" },
                            { key: "panel_damage",         label: "Panel Damage",         type: "select", options: ["Yes", "No"] },
                            { key: "panel_brand",          label: "Panel Brand",          type: "text",   placeholder: "e.g. Canadian Solar" },
                            { key: "inverter_alarm",       label: "Inverter Alarm",       type: "select", options: ["Yes", "No"] },
                            { key: "alarm_code",           label: "Alarm Code",           type: "text",   placeholder: "e.g. E001" },
                            { key: "potential_shading",    label: "Potential Shading",    type: "select", options: ["Yes", "No"] },
                            { key: "total_panels_cleaned", label: "Total Panels Cleaned", type: "number", placeholder: "e.g. 150" },
                          ].map((f) => (
                            <div key={f.key} className="jh-field-cell">
                              <div className="jh-label">{f.label}</div>
                              {isEditing ? (
                                f.type === "select" ? (
                                  <select
                                    className="jh-input"
                                    value={editData?.[f.key] || ""}
                                    onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })}
                                  >
                                    <option value="">Select…</option>
                                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                ) : (
                                  <input
                                    type={f.type}
                                    className="jh-input"
                                    value={editData?.[f.key] || ""}
                                    onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })}
                                    placeholder={f.placeholder}
                                  />
                                )
                              ) : (
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                                  {selectedJob[f.key] || "N/A"}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Shading Details */}
                        {(selectedJob.potential_shading === "Yes" || (isEditing && editData?.potential_shading === "Yes")) && (
                          <div className="jh-field-cell" style={{ marginBottom: 12 }}>
                            <div className="jh-label">Shading Details</div>
                            {isEditing ? (
                              <textarea
                                className="jh-input"
                                value={editData?.shading_details || ""}
                                onChange={(e) => setEditData({ ...editData, shading_details: e.target.value })}
                                rows={3}
                                placeholder="Describe shading issues…"
                                style={{ resize: "vertical" }}
                              />
                            ) : (
                              <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                                {selectedJob.shading_details || "No details provided"}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Boolean Checkboxes */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 9, marginTop: 4 }}>
                          {[
                            { label: "Rusting",       key: "rusting",        icon: "🔧" },
                            { label: "Bird Dropping", key: "bird_dropping",  icon: "🦅" },
                            { label: "Moss & Debris", key: "mos_and_debris", icon: "🍃" },
                            { label: "Earthing",      key: "earthing",       icon: "⚡" },
                          ].map((field) => {
                            const active = isEditing ? !!editData?.[field.key] : !!selectedJob[field.key];
                            return (
                              <div
                                key={field.key}
                                className={`jh-check-cell ${active ? "active-check" : ""}`}
                                style={{ cursor: isEditing ? "pointer" : "default" }}
                                onClick={() => {
                                  if (isEditing) {
                                    setEditData({ ...editData, [field.key]: !editData?.[field.key] });
                                  }
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                  <span style={{ fontSize: 16 }}>{field.icon}</span>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>{field.label}</span>
                                </div>
                                {isEditing ? (
                                  <input
                                    type="checkbox"
                                    checked={editData?.[field.key] || false}
                                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.checked })}
                                    style={{ width: 16, height: 16, cursor: "pointer", accentColor: T.green }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                ) : (
                                  <span style={{ fontSize: 14, fontWeight: 700, color: active ? T.green : "var(--nd-w20)" }}>
                                    {active ? "✓" : "✗"}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Additional Notes */}
                        <div className="jh-field-cell" style={{ marginTop: 12 }}>
                          <div className="jh-label">Additional Notes</div>
                          {isEditing ? (
                            <textarea
                              className="jh-input"
                              value={editData?.additional_notes || ""}
                              onChange={(e) => setEditData({ ...editData, additional_notes: e.target.value })}
                              rows={3}
                              placeholder="Any additional observations…"
                              style={{ resize: "vertical" }}
                            />
                          ) : (
                            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                              {selectedJob.additional_notes || "No additional notes"}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                  {/* Customer Notes */}
                  {(selectedJob.notes || isEditing) && (
                    <div style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.22)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                      <SecHead icon={NotesIcon} label="Notes" color={T.amber} />
                      {isEditing ? (
                        <textarea
                          className="jh-input"
                          value={editData?.notes || ""}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          rows={3}
                          style={{ resize: "vertical" }}
                        />
                      ) : (
                        <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                          {selectedJob.notes}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Customer Signature */}
                  {selectedJob.customer_signature && (
                    <div className="jh-field-cell" style={{ marginBottom: 16 }}>
                      <div className="jh-label" style={{ marginBottom: 8 }}>Customer Signature</div>
                      <img
                        src={
                          selectedJob.customer_signature.startsWith("http")
                            ? selectedJob.customer_signature
                            : `https://app.sowashusa.com/uploads/signatures/${selectedJob.customer_signature}`
                        }
                        alt="Customer Signature"
                        style={{
                          width: "100%", maxWidth: 280, height: 90, objectFit: "contain",
                          border: `1px solid ${T.border}`, borderRadius: 9, background: "var(--nd-w03)",
                        }}
                      />
                    </div>
                  )}

                  {/* FSR Photo */}
                  {selectedJob.fsr_photo && (
                    <div className="jh-field-cell" style={{ marginBottom: 16 }}>
                      <div className="jh-label" style={{ marginBottom: 8 }}>FSR Photo</div>
                      <AutoFallbackImage
                        photoRaw={selectedJob.fsr_photo}
                        alt="FSR"
                        isEditing={false}
                        customStyle={{
                          maxWidth: 260, height: 180, objectFit: "cover",
                          border: `1px solid ${T.border}`, borderRadius: 9,
                          cursor: "pointer", background: "var(--nd-w03)",
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Modal footer */}
                <div style={{ padding: "13px 22px", borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10, background: "var(--nd-w02)", flexShrink: 0 }}>
                  {/* ── Approval Panel (completed jobs only, view mode only) ── */}
                  {!isEditing && selectedJob.status === "completed" && (() => {
                    const ab = getApprovalBadge(selectedJob.approval_status);
                    return (
                      <div style={{ width: "100%", padding: "12px 14px", borderRadius: 11, background: ab.bg, border: `1px solid ${ab.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                          {/* Status label */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: ab.color }}>
                              {ab.icon} Client Visibility: {ab.label}
                            </span>
                            {selectedJob.rejection_reason && (
                              <span style={{ fontSize: 11, color: T.muted, fontStyle: "italic" }}>
                                — "{selectedJob.rejection_reason}"
                              </span>
                            )}
                          </div>
                          {/* Action buttons */}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {selectedJob.approval_status !== "approved" && (
                              <button className="jh-btn-approve" onClick={handleApprove} disabled={approving}>
                                {approving
                                  ? <><span style={{ width: 12, height: 12, border: "2px solid rgba(52,211,153,0.35)", borderTop: "2px solid #34D399", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} /> Approving…</>
                                  : <><ApproveIcon style={{ fontSize: 15 }} /> Approve</>
                                }
                              </button>
                            )}
                            {selectedJob.approval_status !== "rejected" && !showRejectInput && (
                              <button className="jh-btn-reject" onClick={() => setShowRejectInput(true)}>
                                <RejectIcon style={{ fontSize: 15 }} /> Reject
                              </button>
                            )}
                            {selectedJob.approval_status !== "pending" && (
                              <button className="jh-btn-undo" onClick={handleUndoApproval}>
                                ↩ Undo
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Reject reason inline input */}
                        {showRejectInput && (
                          <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
                            <input
                              type="text"
                              className="jh-input"
                              placeholder="Reason for rejection (optional)"
                              value={rejectReason}
                              onChange={e => setRejectReason(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") handleReject(); if (e.key === "Escape") { setShowRejectInput(false); setRejectReason(""); } }}
                              autoFocus
                              style={{ flex: 1, minWidth: 180, borderColor: "rgba(248,113,113,0.35)" }}
                            />
                            <button className="jh-btn-reject" onClick={handleReject} disabled={rejecting} style={{ whiteSpace: "nowrap" }}>
                              {rejecting ? "Rejecting…" : "Confirm Reject"}
                            </button>
                            <button className="jh-btn-undo" onClick={() => { setShowRejectInput(false); setRejectReason(""); }}>
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Standard actions ── */}
                  <div style={{ display: "flex", gap: 10, width: "100%" }}>
                    {isEditing ? (
                      <>
                        <button
                          className="jh-btn-ghost"
                          style={{ flex: 1 }}
                          onClick={() => { setIsEditing(false); setEditData(null); }}
                        >
                          Cancel
                        </button>
                        <button
                          className="jh-btn-primary"
                          style={{ flex: 1, background: "linear-gradient(135deg,#34D399,#059669)", boxShadow: "0 4px 14px rgba(52,211,153,0.28)" }}
                          onClick={handleUpdate}
                          disabled={saving}
                        >
                          {saving
                            ? <><span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,0.35)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} /> Saving…</>
                            : "Save Changes"
                          }
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="jh-btn-ghost" style={{ flex: 1 }} onClick={() => setDetailsOpen(false)}>
                          Close
                        </button>
                        <button
                          className="jh-btn-primary"
                          style={{
                            flex: 1,
                            background: selectedJob.job_type === "maintenance"
                              ? "linear-gradient(135deg,#A78BFA,#7C3AED)"
                              : "linear-gradient(135deg,#3B82F6,#22D3A5)",
                            boxShadow: selectedJob.job_type === "maintenance"
                              ? "0 4px 14px rgba(167,139,250,0.28)"
                              : "0 4px 14px rgba(59,130,246,0.28)",
                          }}
                          onClick={handleDownloadReport}
                          disabled={downloadLoading}
                        >
                          {downloadLoading
                            ? <><span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,0.35)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} /> Generating…</>
                            : <><DownloadIcon style={{ fontSize: 16 }} /> Download Report</>
                          }
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )
          )}
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default JobHistory;